<script setup lang="ts">
  import ChatFolderDialog from '@/components/app/chat/global/components/ChatFolderDialog.vue'
  import DeleteChatDialog from '@/components/app/chat/components/DeleteChatDialog.vue'
  import RenameChatDialog from '@/components/app/chat/components/RenameChatDialog.vue'
  import SpinnerLoader from '@/components/ui/SpinnerLoader.vue'
  import { SESSION_OPTIMISTIC_REMOVED_EVENT } from '@/composables/useChatSessionHistory'
  import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle
  } from '@/components/ui/alert-dialog'
  import { Dialog, DialogContent } from '@/components/ui/dialog'
  import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger
  } from '@/components/ui/dropdown-menu'
  import { useChatPdfExport } from '@/composables/useChatPdfExport'
  import { useChatRename } from '@/composables/useChatRename'
  import { useTranslation } from '@/composables/useTranslation'
  import { useGlobalChatStore } from '@/store/app/globalChat/globalChatStore'
  import { useUnifiedChatStore } from '@/store/app/unifiedChat'
  import { useUserStore } from '@/shared/stores/userStore'
  import {
    AiChatQuestionTemplatesService,
    AiChatSessionGroupsService,
    AiChatSessionsService,
    ChatPreferencesService
  } from '@/types/generated/services.gen'
  import type {
    AIChatSession,
    AiChatQuestionTemplate,
    ChatPreference,
    SessionGroup
  } from '@/types/generated/types.gen'
  import { useLawFieldStore } from '@/store/lawFieldStore'
  import { useUserStore as useUserStoreGlobal } from '@/store/userStore'
  import {
    ChevronDown,
    ChevronLeft,
    Copy,
    Download,
    Folder,
    FolderClosed,
    FolderPlus,
    FolderSymlink,
    ListRestart,
    MessagesSquare,
    MoreHorizontal,
    Pencil,
    Plus,
    RotateCcw,
    Search,
    Settings,
    Star,
    Trash2,
    X
  } from 'lucide-vue-next'
  import { useDebounceFn } from '@/composables/useDebounce'
  import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
  import { genericErrorToast } from '@/lib/toasts'
  import { extractMessageText, getSessionDisplayTitle } from '@/utils/chatSessionTitle'

  const props = defineProps<{
    open: boolean
    initialTab?: 'chats' | 'folders' | 'prompts' | 'settings'
    moveSessionTitle?: string | null
    moveSessionUuid?: string | null
  }>()

  const emit = defineEmits<{
    (e: 'update:open', value: boolean): void
    (e: 'selectSession', uuid: string): void
    (e: 'usePrompt', prompt: string): void
    (e: 'moveToFolder', data: { sessionUuid: string; folderUuid: string }): void
    (e: 'sessionsChanged'): void
  }>()

  const userStore = useUserStore()

  const { t } = useTranslation({
    en: {
      chats: 'Chats',
      folders: 'Folders',
      prompts: 'Prompts',
      settings: 'Settings',
      mine: 'My',
      team: 'Team',
      all: 'All',
      search: 'Search',
      noChatsFound: 'No chats found',
      noChatsYet: "You don't have any chats yet. Start a conversation to see it here.",
      noTeamChats: 'No team chats available.',
      noSearchResults: 'No chats match your search.',
      noFoldersFound: 'No folders found',
      noFoldersYet: "You don't have any folders yet. Create one to organize your chats.",
      noFoldersSearchResults: 'No folders match your search.',
      selectLawField: 'Select law field',
      noPromptsFound: 'No prompts found',
      noPromptsYet: 'No prompt templates available yet.',
      noPromptsSearchResults: 'No prompts match your search.',
      createPromptTemplate: 'Create new prompt template',
      title: 'Title',
      lawField: 'Law field',
      lawTopic: 'Law topic',
      prompt: 'Prompt',
      description: 'Description',
      select: 'select',
      promptPlaceholder: 'Enter the prompt text to be used as a template',
      descriptionPlaceholder: 'Enter a description for this template',
      generateWithAi: 'Generate with AI',
      cancel: 'Cancel',
      createTemplate: 'Create prompt template',
      characters: 'characters',
      allSettings: 'All settings',
      shared: 'Shared',
      personal: 'Personal',
      noSettingsFound: 'No settings found',
      noSettingsYet: 'No settings available yet.',
      noSettingsSearchResults: 'No settings match your search.',
      createSetting: 'Create new setting',
      visibility: 'Visibility',
      selectVisibility: 'Select who can access this preference...',
      personalPreference: 'Personal preference (only visible to you)',
      sharedPreference: 'Shared preference (visible to everyone in your whole team)',
      content: 'Content',
      contentPlaceholder: 'Enter the content for this setting',
      createSettingBtn: 'Create setting',
      moveToFolder: 'Move',
      moveHeader: 'move to folder',
      defaultFolder: 'Default',
      moveSessionToFolder: 'Move to folder',
      assignToFolders: 'Add to folders',
      assignPromptToFolder: 'Add to folder',
      assignPromptHeader: 'Add prompt to folder',
      saveTemplate: 'Save',
      usePrompt: 'Use prompt',
      editPrompt: 'Edit prompt',
      copyPrompt: 'Duplicate prompt',
      chatBadge: 'Chat',
      promptBadge: 'Prompt',
      untitledChat: 'Untitled chat',
      moreOptions: 'More options',
      downloadChatAsPdf: 'Download Chat as PDF',
      renameChat: 'Rename',
      organizeChat: 'Organize',
      deleteChat: 'Delete Chat',
      renameFolder: 'Rename',
      deleteFolder: 'Delete',
      deleteFolderTitle: 'Delete folder',
      deleteFolderDescription:
        'Are you sure you want to delete this folder? All chats and prompts inside will be permanently removed. This action cannot be undone.',
      promptMenuEdit: 'Edit',
      promptMenuDuplicate: 'Duplicate',
      promptMenuDelete: 'Delete',
      deletePromptTitle: 'Delete prompt',
      deletePromptDescription:
        'Are you sure you want to delete this prompt template? This action cannot be undone.',
      deletePromptAction: 'Delete',
      editSetting: 'Edit setting',
      saveSetting: 'Save',
      settingMenuEdit: 'Edit',
      settingMenuDelete: 'Delete',
      deleteSettingTitle: 'Delete setting',
      deleteSettingDescription:
        'Are you sure you want to delete this setting? This action cannot be undone.',
      deleteSettingAction: 'Delete'
    },
    de: {
      chats: 'Chats',
      folders: 'Ordner',
      prompts: 'Prompts',
      settings: 'Einstellungen',
      mine: 'Meine',
      team: 'Team',
      all: 'Alle',
      search: 'Suchen',
      noChatsFound: 'Keine Chats gefunden',
      noChatsYet:
        'Sie haben noch keine Chats. Starten Sie eine Konversation, um sie hier zu sehen.',
      noTeamChats: 'Keine Team-Chats verfügbar.',
      noSearchResults: 'Keine Chats entsprechen Ihrer Suche.',
      noFoldersFound: 'Keine Ordner gefunden',
      noFoldersYet:
        'Sie haben noch keine Ordner. Erstellen Sie einen, um Ihre Chats zu organisieren.',
      noFoldersSearchResults: 'Keine Ordner entsprechen Ihrer Suche.',
      selectLawField: 'Rechtsgebiet wählen',
      noPromptsFound: 'Keine Prompts gefunden',
      noPromptsYet: 'Noch keine Prompt-Vorlagen verfügbar.',
      noPromptsSearchResults: 'Keine Prompts entsprechen Ihrer Suche.',
      createPromptTemplate: 'Neue Promptvorlage erstellen',
      title: 'Titel',
      lawField: 'Rechtsgebiet',
      lawTopic: 'Rechtsthema',
      prompt: 'Prompt',
      description: 'Beschreibung',
      select: 'auswählen',
      promptPlaceholder: 'Geben Sie den Prompttext ein, der als Vorlage verwendet werden soll',
      descriptionPlaceholder: 'Geben Sie eine Beschreibung für diese Vorlage ein',
      generateWithAi: 'Mit KI generieren',
      cancel: 'Abbrechen',
      createTemplate: 'Promptvorlage erstellen',
      characters: 'characters',
      allSettings: 'Alle Einstellungen',
      shared: 'Geteilt',
      personal: 'Persönlich',
      noSettingsFound: 'Keine Einstellungen gefunden',
      noSettingsYet: 'Noch keine Einstellungen verfügbar.',
      noSettingsSearchResults: 'Keine Einstellungen entsprechen Ihrer Suche.',
      createSetting: 'Neue Einstellung',
      visibility: 'Sichtbarkeit',
      selectVisibility: 'Wählen Sie, wer Zugriff auf diese Einstellung hat...',
      personalPreference: 'Persönliche Einstellung (nur sichtbar für Sie)',
      sharedPreference: 'Geteilte Einstellung (sichtbar für alle in Ihrem Team)',
      content: 'Inhalt',
      contentPlaceholder: 'Geben Sie den Inhalt für diese Einstellung ein',
      createSettingBtn: 'Einstellung erstellen',
      moveToFolder: 'Verschieben',
      moveHeader: 'verschieben',
      defaultFolder: 'Standard',
      moveSessionToFolder: 'In Ordner verschieben',
      assignToFolders: 'Zu Ordnern hinzufügen',
      assignPromptToFolder: 'Zu Ordner hinzufügen',
      assignPromptHeader: 'Prompt zu Ordner hinzufügen',
      saveTemplate: 'Speichern',
      usePrompt: 'Prompt verwenden',
      editPrompt: 'Prompt bearbeiten',
      copyPrompt: 'Prompt duplizieren',
      chatBadge: 'Chat',
      promptBadge: 'Prompt',
      untitledChat: 'Unbenannter Chat',
      moreOptions: 'Weitere Optionen',
      downloadChatAsPdf: 'Chat als PDF herunterladen',
      renameChat: 'Umbenennen',
      organizeChat: 'Organisieren',
      deleteChat: 'Chat löschen',
      renameFolder: 'Umbenennen',
      deleteFolder: 'Löschen',
      deleteFolderTitle: 'Ordner löschen',
      deleteFolderDescription:
        'Sind Sie sicher, dass Sie diesen Ordner löschen möchten? Alle darin enthaltenen Chats und Prompts werden dauerhaft entfernt. Diese Aktion kann nicht rückgängig gemacht werden.',
      promptMenuEdit: 'Bearbeiten',
      promptMenuDuplicate: 'Duplizieren',
      promptMenuDelete: 'Löschen',
      deletePromptTitle: 'Prompt löschen',
      deletePromptDescription:
        'Möchten Sie diese Prompt-Vorlage wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.',
      deletePromptAction: 'Löschen',
      editSetting: 'Einstellung bearbeiten',
      saveSetting: 'Speichern',
      settingMenuEdit: 'Bearbeiten',
      settingMenuDelete: 'Löschen',
      deleteSettingTitle: 'Einstellung löschen',
      deleteSettingDescription:
        'Möchten Sie diese Einstellung wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.',
      deleteSettingAction: 'Löschen'
    },
    fr: {
      chats: 'Chats',
      folders: 'Dossiers',
      prompts: 'Prompts',
      settings: 'Paramètres',
      mine: 'Mes',
      team: 'Équipe',
      all: 'Tous',
      search: 'Rechercher',
      noChatsFound: 'Aucun chat trouvé',
      noChatsYet: "Vous n'avez pas encore de chats. Démarrez une conversation pour la voir ici.",
      noTeamChats: "Aucun chat d'équipe disponible.",
      noSearchResults: 'Aucun chat ne correspond à votre recherche.',
      noFoldersFound: 'Aucun dossier trouvé',
      noFoldersYet: "Vous n'avez pas encore de dossiers. Créez-en un pour organiser vos chats.",
      noFoldersSearchResults: 'Aucun dossier ne correspond à votre recherche.',
      selectLawField: 'Domaine juridique',
      noPromptsFound: 'Aucun prompt trouvé',
      noPromptsYet: 'Aucun modèle de prompt disponible.',
      noPromptsSearchResults: 'Aucun prompt ne correspond à votre recherche.',
      createPromptTemplate: 'Créer un nouveau modèle de prompt',
      title: 'Titre',
      lawField: 'Domaine juridique',
      lawTopic: 'Sujet juridique',
      prompt: 'Prompt',
      description: 'Description',
      select: 'sélectionner',
      promptPlaceholder: 'Entrez le texte du prompt à utiliser comme modèle',
      descriptionPlaceholder: 'Entrez une description pour ce modèle',
      generateWithAi: 'Générer avec IA',
      cancel: 'Annuler',
      createTemplate: 'Créer modèle de prompt',
      characters: 'caractères',
      allSettings: 'Tous les paramètres',
      shared: 'Partagé',
      personal: 'Personnel',
      noSettingsFound: 'Aucun paramètre trouvé',
      noSettingsYet: 'Aucun paramètre disponible.',
      noSettingsSearchResults: 'Aucun paramètre ne correspond à votre recherche.',
      createSetting: 'Nouveau paramètre',
      visibility: 'Visibilité',
      selectVisibility: 'Sélectionnez qui peut accéder à cette préférence...',
      personalPreference: 'Préférence personnelle (visible uniquement par vous)',
      sharedPreference: "Préférence partagée (visible par toute l'équipe)",
      content: 'Contenu',
      contentPlaceholder: 'Entrez le contenu pour ce paramètre',
      createSettingBtn: 'Créer paramètre',
      moveToFolder: 'Déplacer',
      moveHeader: 'déplacer vers le dossier',
      defaultFolder: 'Défaut',
      moveSessionToFolder: 'Déplacer vers dossier',
      assignToFolders: 'Ajouter aux dossiers',
      assignPromptToFolder: 'Ajouter au dossier',
      assignPromptHeader: 'Ajouter le prompt au dossier',
      saveTemplate: 'Enregistrer',
      usePrompt: 'Utiliser le prompt',
      editPrompt: 'Modifier le prompt',
      copyPrompt: 'Dupliquer le prompt',
      chatBadge: 'Chat',
      promptBadge: 'Prompt',
      untitledChat: 'Chat sans titre',
      moreOptions: "Plus d'options",
      downloadChatAsPdf: 'Télécharger le chat en PDF',
      renameChat: 'Renommer',
      organizeChat: 'Organiser',
      deleteChat: 'Supprimer le chat',
      renameFolder: 'Renommer',
      deleteFolder: 'Supprimer',
      deleteFolderTitle: 'Supprimer le dossier',
      deleteFolderDescription:
        'Êtes-vous sûr de vouloir supprimer ce dossier ? Tous les chats et prompts qu’il contient seront définitivement supprimés. Cette action est irréversible.',
      promptMenuEdit: 'Modifier',
      promptMenuDuplicate: 'Dupliquer',
      promptMenuDelete: 'Supprimer',
      deletePromptTitle: 'Supprimer le prompt',
      deletePromptDescription:
        'Voulez-vous vraiment supprimer ce modèle de prompt ? Cette action est irréversible.',
      deletePromptAction: 'Supprimer',
      editSetting: 'Modifier le paramètre',
      saveSetting: 'Enregistrer',
      settingMenuEdit: 'Modifier',
      settingMenuDelete: 'Supprimer',
      deleteSettingTitle: 'Supprimer le paramètre',
      deleteSettingDescription:
        'Voulez-vous vraiment supprimer ce paramètre ? Cette action est irréversible.',
      deleteSettingAction: 'Supprimer'
    }
  })

  const PAGE_SIZE = 25

  // Local move mode — initiated from within the Chats tab
  const localMoveSessionUuid = ref<string | null>(null)
  const localMoveSessionTitle = ref<string | null>(null)

  const isMoveMode = computed(() => !!props.moveSessionTitle || !!localMoveSessionUuid.value)
  const effectiveMoveSessionUuid = computed(
    () => props.moveSessionUuid || localMoveSessionUuid.value
  )
  const effectiveMoveSessionTitle = computed(
    () => props.moveSessionTitle || localMoveSessionTitle.value
  )

  function startLocalMove(session: AIChatSession) {
    localMoveSessionUuid.value = session.uuid
    localMoveSessionTitle.value = session.title || getLastMessage(session) || session.uuid
    selectedFolderUuid.value = null
    activeSidebarItem.value = 'folders'
  }

  function cancelLocalMove() {
    localMoveSessionUuid.value = null
    localMoveSessionTitle.value = null
    activeSidebarItem.value = 'chats'
  }

  // ========== Per-row action menu (Download / Rename / Organize / Delete) ==========
  // The chat list shows a kebab menu on hover. Download streams a PDF directly,
  // while Rename and Delete open shared dialogs scoped to ``actionSession`` so
  // the same dialog instance can target any row without leaking context between
  // clicks. Successful mutations emit ``sessionsChanged`` so the parent header
  // bypasses its history-popover refresh cooldown and shows the new state on
  // the next open.
  const actionSession = ref<AIChatSession | null>(null)
  const showRowRenameDialog = ref(false)
  const showRowDeleteDialog = ref(false)

  const { exportSessionPdf } = useChatPdfExport()
  const { renameSession: renameRowSession } = useChatRename({
    onAfterCommit: () => emit('sessionsChanged')
  })
  const unifiedChatStore = useUnifiedChatStore()
  const globalChatStore = useGlobalChatStore()

  function handleRowDownloadPdf(session: AIChatSession) {
    if (!session.uuid) return
    exportSessionPdf(session.uuid)
  }

  function handleRowRename(session: AIChatSession) {
    actionSession.value = session
    showRowRenameDialog.value = true
  }

  function handleRowDelete(session: AIChatSession) {
    actionSession.value = session
    showRowDeleteDialog.value = true
  }

  async function onRowRenameConfirmed(title: string) {
    const session = actionSession.value
    if (!session) return
    const outcome = await renameRowSession(session, title)
    if (outcome.status === 'committed' && outcome.session) {
      // Mirror the new title into the local list so the row shows the renamed
      // value immediately without waiting for the next ``fetchSessions``.
      const idx = sessions.value.findIndex((s) => s.uuid === session.uuid)
      if (idx !== -1) sessions.value[idx] = outcome.session
    }
    actionSession.value = null
  }

  async function onRowDeleteConfirmed() {
    const session = actionSession.value
    if (!session?.uuid) {
      showRowDeleteDialog.value = false
      return
    }
    const uuid = session.uuid
    try {
      // ``unifiedChat.deleteSession`` calls the destroy API and clears any
      // active session slot that matches (side panel + fullscreen) plus the
      // persisted side-panel snapshot. Mirror the removal into the legacy
      // global-chat list so the case sidebar / fullscreen popover fallback
      // do not retain a stale entry.
      await unifiedChatStore.deleteSession(uuid)
      globalChatStore.removeChatSession(uuid)
      sessions.value = sessions.value.filter((s) => s.uuid !== uuid)
      // Drop the deleted session from any folder's cached contents so the
      // folders tab re-renders accurately on the next expand.
      for (const folderUuid of Object.keys(folderSessionsMap.value)) {
        folderSessionsMap.value[folderUuid] = folderSessionsMap.value[folderUuid].filter(
          (item: any) => item.uuid !== uuid
        )
      }
      emit('sessionsChanged')
    } catch {
      genericErrorToast()
    } finally {
      showRowDeleteDialog.value = false
      actionSession.value = null
    }
  }

  // Prompt assign mode — initiated from within the Prompts tab (FolderInput button)
  const promptToAssign = ref<AiChatQuestionTemplate | null>(null)
  const isPromptAssignMode = computed(() => !!promptToAssign.value)

  function startPromptAssign(prompt: AiChatQuestionTemplate, event: Event) {
    event.stopPropagation()
    promptToAssign.value = prompt
    selectedFolderUuid.value = null
    activeSidebarItem.value = 'folders'
  }

  function cancelPromptAssign() {
    promptToAssign.value = null
    activeSidebarItem.value = 'prompts'
  }

  async function handleAssignPromptToFolder() {
    if (!selectedFolderUuid.value || !promptToAssign.value) return
    const prompt = promptToAssign.value
    const currentFolders = ((prompt as any).folders as string[] | undefined) ?? []
    const newFolderUuid = selectedFolderUuid.value
    try {
      await AiChatQuestionTemplatesService.aiChatQuestionTemplatesPartialUpdate({
        uuid: prompt.uuid,
        requestBody: {
          folders: currentFolders.includes(newFolderUuid)
            ? currentFolders
            : [...currentFolders, newFolderUuid]
        } as any
      })
      // Update locally so subsequent clicks have the right folder list
      const idx = prompts.value.findIndex((p) => p.uuid === prompt.uuid)
      if (idx !== -1) {
        ;(prompts.value[idx] as any).folders = currentFolders.includes(newFolderUuid)
          ? currentFolders
          : [...currentFolders, newFolderUuid]
      }
    } catch {
      genericErrorToast()
    }
    // Invalidate the target folder's cache so it re-fetches the prompt on next expand
    delete folderSessionsMap.value[newFolderUuid]
    expandedFolders.value.delete(newFolderUuid)
    cancelPromptAssign()
  }

  // Editing an existing prompt — reuses the create form fields
  const editingPrompt = ref<AiChatQuestionTemplate | null>(null)

  function openPromptEditFromFolder(item: any) {
    if ((item as AiChatQuestionTemplate).is_jupus_template) {
      handleCopyPrompt(item as AiChatQuestionTemplate)
    } else {
      activeSidebarItem.value = 'prompts'
      openEditForm(item as AiChatQuestionTemplate)
    }
  }

  function openEditForm(prompt: AiChatQuestionTemplate) {
    editingPrompt.value = prompt
    createTitle.value = prompt.title ?? ''
    createPrompt.value = prompt.prompt ?? ''
    createDescription.value = (prompt.description as string | null | undefined) ?? ''
    createLawField.value = (prompt.law_field as string | null | undefined) ?? ''
    createLawTopic.value = (prompt.law_topic as string | null | undefined) ?? ''
    createFolderUuids.value = ((prompt as any).folders as string[] | undefined) ?? []
    showCreateForm.value = true
    if (folders.value.length === 0) fetchFolders()
  }

  const selectedFolderUuid = ref<string | null>(null)

  const activeSidebarItem = ref<'chats' | 'folders' | 'prompts' | 'settings'>('chats')

  const activeTab = ref<'mine' | 'team' | 'all'>('mine')
  const searchQuery = ref('')
  const folderSearchQuery = ref('')
  const sessions = ref<AIChatSession[]>([])
  const optimisticallyRemovedSessionUuids = ref<Set<string>>(new Set())
  const folders = ref<SessionGroup[]>([])
  const expandedFolders = ref<Set<string>>(new Set())
  const folderSessionsMap = ref<Record<string, any[]>>({})
  const loading = ref(false)
  const loadingMore = ref(false)
  const hasMoreSessions = ref(true)
  const foldersLoading = ref(false)
  const foldersLoadingMore = ref(false)
  const hasMoreFolders = ref(true)
  const chatListRef = ref<HTMLElement | null>(null)
  const folderListRef = ref<HTMLElement | null>(null)

  // Prompts state
  const promptSearchQuery = ref('')
  const promptLawFieldFilter = ref<string | null>(null)
  const prompts = ref<AiChatQuestionTemplate[]>([])
  const promptsLoading = ref(false)
  const showLawFieldDropdown = ref(false)

  function hideOptimisticallyRemovedSession(sessionUuid: string): void {
    if (!sessionUuid) return
    optimisticallyRemovedSessionUuids.value = new Set([
      ...optimisticallyRemovedSessionUuids.value,
      sessionUuid
    ])
    sessions.value = sessions.value.filter((session) => session.uuid !== sessionUuid)
  }

  function onSessionOptimisticallyRemoved(event: Event): void {
    const customEvent = event as CustomEvent<{ sessionUuid?: string }>
    const sessionUuid = customEvent.detail?.sessionUuid
    if (!sessionUuid) return
    hideOptimisticallyRemovedSession(sessionUuid)
  }

  onMounted(() => {
    if (typeof window === 'undefined') return
    window.addEventListener(SESSION_OPTIMISTIC_REMOVED_EVENT, onSessionOptimisticallyRemoved)
  })

  onBeforeUnmount(() => {
    if (typeof window === 'undefined') return
    window.removeEventListener(SESSION_OPTIMISTIC_REMOVED_EVENT, onSessionOptimisticallyRemoved)
  })

  async function fetchPrompts() {
    promptsLoading.value = true
    try {
      const res = await AiChatQuestionTemplatesService.aiChatQuestionTemplatesList({
        limit: 0 // fetch all — same pattern as globalChatStore
      })
      prompts.value = res.results ?? []
    } catch {
      genericErrorToast()
    } finally {
      promptsLoading.value = false
    }
  }

  const filteredPrompts = computed(() => {
    let result = prompts.value
    const q = promptSearchQuery.value.toLowerCase().trim()

    if (promptLawFieldFilter.value) {
      result = result.filter((p) => p.law_field === promptLawFieldFilter.value)
    }

    if (q) {
      result = result.filter((p) => {
        const title = (p.title ?? '').toLowerCase()
        const desc = (p.description ?? '').toLowerCase()
        const prompt = (p.prompt ?? '').toLowerCase()
        return title.includes(q) || desc.includes(q) || prompt.includes(q)
      })
    }

    return result
  })

  const availableLawFields = computed(() => {
    const fields = new Set<string>()
    for (const p of prompts.value) {
      if (p.law_field) fields.add(p.law_field as string)
    }
    return Array.from(fields).sort()
  })

  function getLawFieldLabel(lawField: string, lawTopic?: string | null): string {
    const parts = [lawField]
    if (lawTopic) parts.push(lawTopic as string)
    return parts.join(' / ')
  }

  const promptEmptyMessage = computed(() => {
    if (promptSearchQuery.value.trim() || promptLawFieldFilter.value)
      return t('noPromptsSearchResults')
    return t('noPromptsYet')
  })

  function handleUsePrompt(prompt: AiChatQuestionTemplate) {
    emit('usePrompt', prompt.prompt)
    emit('update:open', false)
  }

  async function handleCopyPrompt(prompt: AiChatQuestionTemplate) {
    try {
      const copy = await AiChatQuestionTemplatesService.aiChatQuestionTemplatesCopyCreate({
        uuid: prompt.uuid
      })
      prompts.value = [copy, ...prompts.value]
    } catch {
      genericErrorToast()
    }
  }

  // Per-row prompt delete — confirmation lives in a local AlertDialog so it can
  // sit on top of this modal (z-[60]) without z-index acrobatics.
  const promptToDelete = ref<AiChatQuestionTemplate | null>(null)
  const showDeletePromptDialog = ref(false)

  function handlePromptRowDelete(prompt: AiChatQuestionTemplate) {
    promptToDelete.value = prompt
    showDeletePromptDialog.value = true
  }

  async function confirmDeletePrompt() {
    const prompt = promptToDelete.value
    if (!prompt?.uuid) {
      showDeletePromptDialog.value = false
      promptToDelete.value = null
      return
    }
    const uuid = prompt.uuid
    try {
      await AiChatQuestionTemplatesService.aiChatQuestionTemplatesDestroy({ uuid })
      prompts.value = prompts.value.filter((p) => p.uuid !== uuid)
      // Drop the deleted prompt from any folder's cached contents so the folders
      // tab re-renders accurately on the next expand without a refetch.
      for (const folderUuid of Object.keys(folderSessionsMap.value)) {
        folderSessionsMap.value[folderUuid] = folderSessionsMap.value[folderUuid].filter(
          (item: any) => item.uuid !== uuid
        )
      }
      if (editingPrompt.value?.uuid === uuid) {
        resetCreateForm()
      }
      if (promptToAssign.value?.uuid === uuid) {
        cancelPromptAssign()
      }
    } catch {
      genericErrorToast()
    } finally {
      showDeletePromptDialog.value = false
      promptToDelete.value = null
    }
  }

  async function toggleFavorite(template: AiChatQuestionTemplate, event: Event) {
    event.stopPropagation()
    try {
      if (template.is_favorite === 'true') {
        await AiChatQuestionTemplatesService.aiChatQuestionTemplatesUnfavoriteCreate({
          uuid: template.uuid,
          requestBody: {}
        })
        template.is_favorite = 'false'
      } else {
        await AiChatQuestionTemplatesService.aiChatQuestionTemplatesFavoriteCreate({
          uuid: template.uuid,
          requestBody: {}
        })
        template.is_favorite = 'true'
      }
    } catch {
      genericErrorToast()
    }
  }

  // ========== Prompt creation form ==========
  const showCreateForm = ref(false)
  const createTitle = ref('')
  const createPrompt = ref('')
  const createDescription = ref('')
  const createLawField = ref('')
  const createLawTopic = ref('')
  const createSaving = ref(false)
  const createGeneratingDesc = ref(false)
  const showCreateLawFieldDropdown = ref(false)
  const showCreateLawTopicDropdown = ref(false)
  const createFolderUuids = ref<string[]>([])

  const TITLE_MAX = 50
  const DESC_MAX = 300

  const globalUserStore = useUserStoreGlobal()
  const { fetchLawFields, getLawFieldOptions, getLawTopicOptions } = useLawFieldStore()

  const lawFieldOptions = computed(() => {
    const user = globalUserStore.user
    if (!user) return []
    return getLawFieldOptions(user)
  })

  const lawTopicOptions = computed(() => {
    if (!createLawField.value) return []
    return getLawTopicOptions(createLawField.value)
  })

  const canCreate = computed(
    () =>
      createTitle.value.trim().length > 0 &&
      createPrompt.value.trim().length > 0 &&
      !createSaving.value
  )

  function selectCreateLawField(value: string) {
    createLawField.value = value
    createLawTopic.value = ''
    showCreateLawFieldDropdown.value = false
  }

  function selectCreateLawTopic(value: string) {
    createLawTopic.value = value
    showCreateLawTopicDropdown.value = false
  }

  // Load folders lazily when the create form opens (may not be loaded if user hasn't visited the Folders tab)
  watch(showCreateForm, (open) => {
    if (open && folders.value.length === 0) {
      fetchFolders()
    }
  })

  function selectPromptLawFieldFilter(field: string | null) {
    promptLawFieldFilter.value = field
    showLawFieldDropdown.value = false
  }

  function resetCreateForm() {
    editingPrompt.value = null
    createTitle.value = ''
    createPrompt.value = ''
    createDescription.value = ''
    createLawField.value = ''
    createLawTopic.value = ''
    createFolderUuids.value = []
    showCreateForm.value = false
  }

  async function handleCreate() {
    if (!canCreate.value) return
    createSaving.value = true
    try {
      if (editingPrompt.value) {
        // Edit mode — PATCH existing prompt
        const updated = await AiChatQuestionTemplatesService.aiChatQuestionTemplatesPartialUpdate({
          uuid: editingPrompt.value.uuid,
          requestBody: {
            title: createTitle.value.trim(),
            prompt: createPrompt.value.trim(),
            description: createDescription.value.trim() || undefined,
            law_field: createLawField.value || undefined,
            law_topic: createLawTopic.value || undefined,
            folders: createFolderUuids.value.length ? createFolderUuids.value : []
          } as any
        })
        // Update in the local list
        const idx = prompts.value.findIndex((p) => p.uuid === editingPrompt.value!.uuid)
        if (idx !== -1) {
          prompts.value[idx] = updated
        }
        resetCreateForm()
      } else {
        // Create mode
        await AiChatQuestionTemplatesService.aiChatQuestionTemplatesCreate({
          requestBody: {
            title: createTitle.value.trim(),
            prompt: createPrompt.value.trim(),
            description: createDescription.value.trim() || undefined,
            law_field: createLawField.value || undefined,
            law_topic: createLawTopic.value || undefined,
            folders: createFolderUuids.value.length ? createFolderUuids.value : undefined
          } as any
        })
        resetCreateForm()
        fetchPrompts()
      }
    } catch {
      genericErrorToast()
    } finally {
      createSaving.value = false
    }
  }

  function handleUsePromptFromForm() {
    const promptText = createPrompt.value.trim()
    if (promptText) {
      emit('usePrompt', promptText)
      emit('update:open', false)
    }
  }

  async function handleGenerateDescription() {
    if (!createPrompt.value.trim() || createGeneratingDesc.value) return
    createGeneratingDesc.value = true
    try {
      const res =
        await AiChatQuestionTemplatesService.aiChatQuestionTemplatesGenerateDescriptionCreate({
          requestBody: {
            prompt: createPrompt.value.trim(),
            title: createTitle.value.trim(),
            law_field: createLawField.value || undefined,
            law_topic: createLawTopic.value || undefined
          }
        })
      if (res.description) {
        createDescription.value = res.description
      }
    } catch {
      genericErrorToast()
    } finally {
      createGeneratingDesc.value = false
    }
  }

  // ========== Settings / Preferences ==========
  const settingsSearchQuery = ref('')
  const settingsFilter = ref<'all' | 'shared' | 'personal'>('all')
  const preferences = ref<ChatPreference[]>([])
  const preferencesLoading = ref(false)
  const showSettingsCreateForm = ref(false)
  const showSettingsFilterDropdown = ref(false)

  // Create / edit form state — ``editingPreference`` distinguishes the two modes:
  // ``null`` → POST a new preference; non-null → PATCH the existing one in place.
  const settingsCreateTitle = ref('')
  const settingsCreateContent = ref('')
  const settingsCreateScope = ref<'ORGANIZATION' | 'USER'>('ORGANIZATION')
  const settingsCreateSaving = ref(false)
  const showSettingsScopeDropdown = ref(false)
  const editingPreference = ref<ChatPreference | null>(null)

  const SETTINGS_TITLE_MAX = 50
  const SETTINGS_CONTENT_MAX = 500

  async function fetchPreferences() {
    preferencesLoading.value = true
    try {
      const res = await ChatPreferencesService.chatPreferencesList({
        limit: 100,
        ordering: '-created_at',
        search: settingsSearchQuery.value || undefined
      })
      preferences.value = res.results ?? []
    } catch {
      genericErrorToast()
    } finally {
      preferencesLoading.value = false
    }
  }

  const filteredPreferences = computed(() => {
    let result = preferences.value
    if (settingsFilter.value === 'shared') {
      result = result.filter((p) => p.scope === 'ORGANIZATION')
    } else if (settingsFilter.value === 'personal') {
      result = result.filter((p) => p.scope === 'USER')
    }
    return result
  })

  const settingsEmptyMessage = computed(() => {
    if (settingsSearchQuery.value.trim() || settingsFilter.value !== 'all') {
      return t('noSettingsSearchResults')
    }
    return t('noSettingsYet')
  })

  const settingsFilterLabel = computed(() => {
    if (settingsFilter.value === 'shared') return t('shared')
    if (settingsFilter.value === 'personal') return t('personal')
    return t('allSettings')
  })

  async function togglePreferenceActive(pref: ChatPreference, event: Event) {
    event.stopPropagation()
    try {
      await ChatPreferencesService.chatPreferencesPartialUpdate({
        uuid: pref.uuid,
        requestBody: { is_active: !pref.is_active }
      })
      pref.is_active = !pref.is_active
    } catch {
      genericErrorToast()
    }
  }

  function getScopeLabel(scope?: string): string {
    if (scope === 'ORGANIZATION') return t('shared')
    if (scope === 'USER') return t('personal')
    return ''
  }

  const canCreateSetting = computed(
    () =>
      settingsCreateTitle.value.trim().length > 0 &&
      settingsCreateContent.value.trim().length > 0 &&
      !settingsCreateSaving.value
  )

  function resetSettingsCreateForm() {
    editingPreference.value = null
    settingsCreateTitle.value = ''
    settingsCreateContent.value = ''
    settingsCreateScope.value = 'ORGANIZATION'
    showSettingsCreateForm.value = false
  }

  function openEditSettingForm(pref: ChatPreference) {
    editingPreference.value = pref
    settingsCreateTitle.value = pref.title ?? ''
    settingsCreateContent.value = pref.content ?? ''
    settingsCreateScope.value = (pref.scope as 'ORGANIZATION' | 'USER') ?? 'ORGANIZATION'
    showSettingsCreateForm.value = true
  }

  function selectSettingsCreateScope(scope: 'ORGANIZATION' | 'USER') {
    settingsCreateScope.value = scope
    showSettingsScopeDropdown.value = false
  }

  function selectSettingsFilter(filter: 'all' | 'shared' | 'personal') {
    settingsFilter.value = filter
    showSettingsFilterDropdown.value = false
  }

  async function handleCreateSetting() {
    if (!canCreateSetting.value) return
    settingsCreateSaving.value = true
    try {
      if (editingPreference.value) {
        const updated = await ChatPreferencesService.chatPreferencesPartialUpdate({
          uuid: editingPreference.value.uuid,
          requestBody: {
            title: settingsCreateTitle.value.trim(),
            content: settingsCreateContent.value.trim(),
            scope: settingsCreateScope.value
          }
        })
        const idx = preferences.value.findIndex((p) => p.uuid === editingPreference.value!.uuid)
        if (idx !== -1) preferences.value[idx] = updated
      } else {
        await ChatPreferencesService.chatPreferencesCreate({
          requestBody: {
            title: settingsCreateTitle.value.trim(),
            content: settingsCreateContent.value.trim(),
            scope: settingsCreateScope.value,
            is_active: true
          }
        })
        fetchPreferences()
      }
      resetSettingsCreateForm()
    } catch {
      genericErrorToast()
    } finally {
      settingsCreateSaving.value = false
    }
  }

  // ===== Per-row preference delete =====
  // Confirmation lives in a local ``AlertDialog`` so it can sit on top of this
  // modal without z-index acrobatics, mirroring the prompt / folder patterns.
  const preferenceToDelete = ref<ChatPreference | null>(null)
  const showDeletePreferenceDialog = ref(false)

  function handlePreferenceRowDelete(pref: ChatPreference) {
    preferenceToDelete.value = pref
    showDeletePreferenceDialog.value = true
  }

  async function confirmDeletePreference() {
    const pref = preferenceToDelete.value
    if (!pref?.uuid) {
      showDeletePreferenceDialog.value = false
      preferenceToDelete.value = null
      return
    }
    const uuid = pref.uuid
    try {
      await ChatPreferencesService.chatPreferencesDestroy({ uuid })
      preferences.value = preferences.value.filter((p) => p.uuid !== uuid)
      if (editingPreference.value?.uuid === uuid) {
        resetSettingsCreateForm()
      }
    } catch {
      genericErrorToast()
    } finally {
      showDeletePreferenceDialog.value = false
      preferenceToDelete.value = null
    }
  }

  const debouncedSettingsFetch = useDebounceFn(() => fetchPreferences(), 300)
  watch(settingsSearchQuery, () => debouncedSettingsFetch())

  let currentFetchId = 0
  let currentFolderFetchId = 0

  async function fetchSessions(append = false) {
    const fetchId = ++currentFetchId
    if (append) {
      loadingMore.value = true
    } else {
      loading.value = true
      sessions.value = []
      hasMoreSessions.value = true
    }
    try {
      const res = await AiChatSessionsService.aiChatSessionsList({
        limit: PAGE_SIZE,
        offset: append ? sessions.value.length : 0,
        ordering: '-created_at',
        search: searchQuery.value || undefined,
        // Server-side scope the ``My`` tab to the current user so we don't paginate
        // through the entire org's sessions (hundreds of rows, loading flicker) just
        // to client-side filter them out. ``Team`` and ``All`` keep the org-wide
        // listing; ``Team`` still excludes the current user via ``filteredSessions``.
        startedByMe: activeTab.value === 'mine' ? true : undefined
      })
      if (fetchId !== currentFetchId) return
      const results = (res.results ?? []).filter(
        (session) => !optimisticallyRemovedSessionUuids.value.has(session.uuid)
      )
      if (append) {
        sessions.value = [...sessions.value, ...results]
      } else {
        sessions.value = results
      }
      hasMoreSessions.value = results.length >= PAGE_SIZE
    } catch {
      genericErrorToast()
    } finally {
      if (fetchId === currentFetchId) {
        loading.value = false
        loadingMore.value = false
      }
    }
  }

  async function fetchFolders(append = false) {
    const fetchId = ++currentFolderFetchId
    if (append) {
      foldersLoadingMore.value = true
    } else {
      foldersLoading.value = true
      folders.value = []
      hasMoreFolders.value = true
    }
    try {
      const res = await AiChatSessionGroupsService.aiChatSessionGroupsList({
        limit: PAGE_SIZE,
        offset: append ? folders.value.length : 0,
        ordering: '-created_at',
        search: folderSearchQuery.value || undefined
      })
      if (fetchId !== currentFolderFetchId) return
      const results = res.results ?? []
      if (append) {
        folders.value = [...folders.value, ...results]
      } else {
        folders.value = results
      }
      hasMoreFolders.value = results.length >= PAGE_SIZE
    } catch {
      genericErrorToast()
    } finally {
      if (fetchId === currentFolderFetchId) {
        foldersLoading.value = false
        foldersLoadingMore.value = false
      }
    }
  }

  async function toggleFolder(folderUuid: string) {
    if (expandedFolders.value.has(folderUuid)) {
      expandedFolders.value.delete(folderUuid)
      return
    }
    if (!folderSessionsMap.value[folderUuid]) {
      try {
        const items = await AiChatSessionGroupsService.aiChatSessionGroupsContentsList({
          uuid: folderUuid
        })
        folderSessionsMap.value[folderUuid] = items ?? []
      } catch {
        folderSessionsMap.value[folderUuid] = []
      }
    }
    expandedFolders.value.add(folderUuid)
  }

  function onChatListScroll() {
    const el = chatListRef.value
    if (!el || loading.value || loadingMore.value || !hasMoreSessions.value) return
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 100) {
      fetchSessions(true)
    }
  }

  function onFolderListScroll() {
    const el = folderListRef.value
    if (!el || foldersLoading.value || foldersLoadingMore.value || !hasMoreFolders.value) return
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 100) {
      fetchFolders(true)
    }
  }

  /**
   * When create/rename folder runs, the main modal closes and ``ChatFolderDialog``
   * opens. On save/cancel we reopen the modal; without this flag the open
   * watcher would always apply ``initialTab`` (typically ``chats``) and the user
   * would land on All chats instead of Ordner / Folders.
   */
  const returnToFoldersTabAfterFolderDialog = ref(false)

  watch(
    () => props.open,
    (isOpen) => {
      if (isOpen) {
        if (isMoveMode.value) {
          activeSidebarItem.value = 'folders'
          selectedFolderUuid.value = null
        } else if (returnToFoldersTabAfterFolderDialog.value) {
          activeSidebarItem.value = 'folders'
          returnToFoldersTabAfterFolderDialog.value = false
        } else if (props.initialTab) {
          activeSidebarItem.value = props.initialTab
        }
        fetchSessions()
        fetchFolders()
        fetchPrompts()
        fetchPreferences()
        fetchLawFields()
      } else {
        // Treat dismissals (overlay click / Escape) as a cancel for any in-modal
        // move-to-folder or prompt-assign flow. Without this the local refs stay
        // primed and the next open — even one explicitly targeting a different
        // tab via ``initialTab`` — would fall back into the move/assign view
        // through the ``isMoveMode`` / ``isPromptAssignMode`` branches above.
        // The parent-driven move (``props.moveSessionUuid``) is reset by the
        // header's ``moveCancelled`` emit; this branch only owns the local refs.
        localMoveSessionUuid.value = null
        localMoveSessionTitle.value = null
        promptToAssign.value = null
        selectedFolderUuid.value = null
      }
    }
  )

  watch(activeTab, () => fetchSessions())
  watch(activeSidebarItem, (item) => {
    if (item === 'folders') fetchFolders()
    if (item === 'prompts' && prompts.value.length === 0) fetchPrompts()
    if (item === 'settings') fetchPreferences()
  })

  const debouncedFetch = useDebounceFn(() => fetchSessions(), 300)
  watch(searchQuery, () => debouncedFetch())

  const debouncedFolderFetch = useDebounceFn(() => fetchFolders(), 300)
  watch(folderSearchQuery, () => debouncedFolderFetch())

  function getInitials(session: AIChatSession): string {
    const user = session.started_by_user_data
    if (!user) return '??'
    const first = (user as any).first_name?.[0] ?? ''
    const last = (user as any).last_name?.[0] ?? ''
    return `${first}${last}`.toUpperCase() || '??'
  }

  function getUserName(session: AIChatSession): string {
    const user = session.started_by_user_data
    if (!user) return ''
    return `${(user as any).first_name ?? ''} ${(user as any).last_name ?? ''}`.trim()
  }

  /**
   * Subtitle shown under the user name in session rows. Delegates to the shared
   * display-title helper so the modal and the active-chat header surface the same
   * string for any given session, with the modal's own "untitled" label as the
   * final fallback when the session has no usable text.
   */
  function getSessionSubtitle(session: AIChatSession): string {
    return getSessionDisplayTitle(session) || t('untitledChat')
  }

  function getLastMessage(session: AIChatSession): string {
    if (!session.messages?.length) return session.description ?? session.title ?? ''
    const text = extractMessageText(session.messages[session.messages.length - 1])
    if (text) return text
    return session.description ?? session.title ?? ''
  }

  const currentUserUuid = computed(() => userStore.user?.uuid)

  const filteredSessions = computed(() => {
    let result = sessions.value.filter(
      (session) => !optimisticallyRemovedSessionUuids.value.has(session.uuid)
    )

    // ``mine`` is scoped server-side via ``startedByMe=true`` in ``fetchSessions``
    // and the fetch generation counter discards stale cross-tab responses, so no
    // client-side filter is needed there.
    // ``team`` still needs a client-side filter: the API has no "started by others"
    // parameter, so we fetch org-wide and hide the current user here. This keeps the
    // pagination count consistent with ``all`` at the cost of a potentially short
    // visible list -- tracked as a follow-up UX improvement.
    // ``all`` → no filter
    if (activeTab.value === 'team') {
      result = result.filter((s) => s.started_by_user !== currentUserUuid.value)
    }

    return result
  })

  const filteredFolders = computed(() => {
    const q = folderSearchQuery.value.toLowerCase().trim()
    if (!q) return folders.value
    return folders.value.filter((f) => f.name.toLowerCase().includes(q))
  })

  const folderEmptyMessage = computed(() => {
    if (folderSearchQuery.value.trim()) return t('noFoldersSearchResults')
    return t('noFoldersYet')
  })

  const emptyStateMessage = computed(() => {
    if (searchQuery.value.trim()) return t('noSearchResults')
    if (activeTab.value === 'team') return t('noTeamChats')
    return t('noChatsYet')
  })

  function handleSelectSession(uuid: string) {
    emit('selectSession', uuid)
    emit('update:open', false)
  }

  async function handleMoveToFolder() {
    if (!selectedFolderUuid.value || !effectiveMoveSessionUuid.value) return

    const sessionUuid = effectiveMoveSessionUuid.value
    const folderUuid = selectedFolderUuid.value

    if (localMoveSessionUuid.value) {
      // Local move: call API directly so we can update modal state immediately
      try {
        await AiChatSessionsService.aiChatSessionsPartialUpdate({
          uuid: sessionUuid,
          requestBody: { session_group_uuid: folderUuid } as any
        })
        // Remove from the Chats tab list
        sessions.value = sessions.value.filter((s) => s.uuid !== sessionUuid)

        // Invalidate only the destination folder's cache so it re-fetches on
        // next expand. Leave other expanded folders intact to avoid jarring collapses.
        delete folderSessionsMap.value[folderUuid]
        expandedFolders.value.delete(folderUuid)
      } catch {
        genericErrorToast()
      } finally {
        cancelLocalMove()
      }
    } else {
      // Prop-driven move: delegate to parent as before
      emit('moveToFolder', { sessionUuid, folderUuid })
      emit('update:open', false)
    }
  }

  const showCreateFolderDialog = ref(false)
  // ``editingFolder`` distinguishes the two modes ``ChatFolderDialog`` exposes:
  // - ``null`` → create flow, prepend the saved folder to the list
  // - non-null → rename flow, replace the folder in place by uuid
  const editingFolder = ref<SessionGroup | null>(null)

  function openCreateFolderDialog() {
    editingFolder.value = null
    returnToFoldersTabAfterFolderDialog.value = true
    emit('update:open', false)
    showCreateFolderDialog.value = true
  }

  function handleFolderRowRename(folder: SessionGroup) {
    editingFolder.value = folder
    returnToFoldersTabAfterFolderDialog.value = true
    emit('update:open', false)
    showCreateFolderDialog.value = true
  }

  function handleFolderSaved(folder: SessionGroup) {
    if (editingFolder.value) {
      const idx = folders.value.findIndex((f) => f.uuid === folder.uuid)
      if (idx !== -1) folders.value[idx] = folder
    } else {
      folders.value = [folder, ...folders.value]
    }
    editingFolder.value = null
    showCreateFolderDialog.value = false
    emit('update:open', true)
  }

  function handleFolderDialogClose() {
    editingFolder.value = null
    showCreateFolderDialog.value = false
    emit('update:open', true)
  }

  // ========== Per-row folder actions (Rename / Delete) ==========
  // Rename reuses the create/edit ``ChatFolderDialog`` via ``editingFolder``.
  // Delete uses a local ``AlertDialog`` so it can sit on top of the modal
  // without z-index acrobatics (``AlertDialogContent`` is already at
  // ``z-[9999]`` while the modal itself is at ``z-[60]``).
  const folderToDelete = ref<SessionGroup | null>(null)
  const showDeleteFolderDialog = ref(false)

  function handleFolderRowDelete(folder: SessionGroup) {
    folderToDelete.value = folder
    showDeleteFolderDialog.value = true
  }

  async function confirmDeleteFolder() {
    const folder = folderToDelete.value
    if (!folder) {
      showDeleteFolderDialog.value = false
      return
    }
    const uuid = folder.uuid
    try {
      // ``globalChatStore.deleteSessionGroup`` calls the destroy API and
      // clears every legacy-store side effect (folder list, child sessions,
      // session/folder processors, document registry, active session +
      // folder slots if they matched). We mirror the removal into the
      // modal's local state so the folders tab + any cached folder bucket
      // re-render immediately without waiting for the next ``fetchFolders``.
      await globalChatStore.deleteSessionGroup(uuid)
      folders.value = folders.value.filter((f) => f.uuid !== uuid)
      delete folderSessionsMap.value[uuid]
      expandedFolders.value.delete(uuid)
      // The backend cascade may have removed this folder's chat sessions; refetch
      // the chats tab so the next switch shows accurate state.
      void fetchSessions()
      // Bypass the parent header's history-popover cooldown so it picks up
      // any cascade-deleted sessions on the next open.
      emit('sessionsChanged')
    } catch {
      genericErrorToast()
    } finally {
      showDeleteFolderDialog.value = false
      folderToDelete.value = null
    }
  }
</script>

<template>
  <Dialog
    :open="props.open"
    @update:open="emit('update:open', $event)"
  >
    <DialogContent
      class="z-[60] flex h-[600px] w-[1050px] max-w-[1050px] items-end justify-start overflow-hidden rounded-2xl
        border-0 bg-white p-0 shadow-[0px_4px_8px_0px_rgba(0,0,0,0.08),0px_8px_12px_0px_rgba(0,0,0,0.08)]
        [&>[data-cy=dialogCloseBtn]]:hidden"
      overlay-class="z-[60]"
    >
      <!-- Left sidebar -->
      <div
        class="inline-flex w-64 flex-col items-start justify-start gap-2.5 self-stretch overflow-hidden bg-gray-50
          px-2.5 py-4"
      >
        <!-- Chats -->
        <button
          type="button"
          class="flex flex-col items-start justify-start gap-4 self-stretch rounded-xl px-4 py-3.5"
          :class="activeSidebarItem === 'chats' ? 'bg-purple-100' : ''"
          :disabled="isMoveMode"
          @click="activeSidebarItem = 'chats'"
        >
          <div class="inline-flex items-center gap-2.5 self-stretch">
            <div class="relative h-5 w-5 overflow-hidden">
              <MessagesSquare
                class="h-5 w-5"
                :class="activeSidebarItem === 'chats' ? 'text-purple-800' : 'text-gray-800'"
                :stroke-width="1.25"
              />
            </div>
            <div
              class="flex-1 text-left text-sm leading-5 font-inter"
              :class="
                activeSidebarItem === 'chats'
                  ? 'font-semibold text-purple-800'
                  : 'font-normal text-gray-800'
              "
            >
              {{ t('chats') }}
            </div>
          </div>
        </button>

        <!-- Divider between Chats and Folders — always mounted with a 1px solid
             top border (border: 1px solid #E5E7EB ≡ neutral-200-stroke) so
             toggling visibility never shifts adjacent items; the active item
             simply renders no visible line above or below itself. -->
        <div
          class="self-stretch border-t border-solid"
          :class="
            activeSidebarItem !== 'chats' && activeSidebarItem !== 'folders'
              ? 'border-gray-200'
              : 'border-transparent'
          "
          aria-hidden="true"
        />

        <!-- Folders -->
        <button
          type="button"
          class="flex flex-col items-start justify-start gap-4 self-stretch rounded-xl px-4 py-3.5"
          :class="activeSidebarItem === 'folders' ? 'bg-purple-100' : ''"
          @click="activeSidebarItem = 'folders'"
        >
          <div class="inline-flex items-center gap-2.5 self-stretch">
            <div class="relative h-5 w-5 overflow-hidden">
              <Folder
                class="h-5 w-5"
                :class="activeSidebarItem === 'folders' ? 'text-purple-800' : 'text-gray-800'"
                :stroke-width="1.25"
              />
            </div>
            <div
              class="flex-1 text-left text-sm leading-5 font-inter"
              :class="
                activeSidebarItem === 'folders'
                  ? 'font-semibold text-purple-800'
                  : 'font-normal text-gray-800'
              "
            >
              {{ t('folders') }}
            </div>
          </div>
        </button>

        <!-- Divider between Folders and Prompts — always mounted, see above. -->
        <div
          class="self-stretch border-t border-solid"
          :class="
            activeSidebarItem !== 'folders' && activeSidebarItem !== 'prompts'
              ? 'border-gray-200'
              : 'border-transparent'
          "
          aria-hidden="true"
        />

        <!-- Prompts -->
        <button
          type="button"
          class="flex flex-col items-start justify-start gap-4 self-stretch rounded-xl px-4 py-3.5"
          :class="activeSidebarItem === 'prompts' ? 'bg-purple-100' : ''"
          :disabled="isMoveMode"
          @click="activeSidebarItem = 'prompts'"
        >
          <div class="inline-flex items-center gap-2.5 self-stretch">
            <div class="relative h-5 w-5 overflow-hidden">
              <ListRestart
                class="h-5 w-5"
                :class="activeSidebarItem === 'prompts' ? 'text-purple-800' : 'text-gray-800'"
                :stroke-width="1.25"
              />
            </div>
            <div
              class="flex-1 text-left text-sm leading-5 font-inter"
              :class="
                activeSidebarItem === 'prompts'
                  ? 'font-semibold text-purple-800'
                  : 'font-normal text-gray-800'
              "
            >
              {{ t('prompts') }}
            </div>
          </div>
        </button>

        <!-- Spacer -->
        <div class="flex-1 self-stretch" />

        <!-- Divider above Settings — always mounted, see above. -->
        <div
          class="self-stretch border-t border-solid"
          :class="activeSidebarItem !== 'settings' ? 'border-gray-200' : 'border-transparent'"
          aria-hidden="true"
        />

        <!-- Settings -->
        <button
          type="button"
          class="flex flex-col items-start justify-start gap-4 self-stretch rounded-xl px-4 py-3.5"
          :class="activeSidebarItem === 'settings' ? 'bg-purple-100' : ''"
          :disabled="isMoveMode"
          @click="activeSidebarItem = 'settings'"
        >
          <div class="inline-flex items-center gap-2.5 self-stretch">
            <div class="relative h-5 w-5 overflow-hidden">
              <Settings
                class="h-5 w-5"
                :class="activeSidebarItem === 'settings' ? 'text-purple-800' : 'text-gray-800'"
                :stroke-width="1.25"
              />
            </div>
            <div
              class="flex-1 text-left text-sm leading-5 font-inter"
              :class="
                activeSidebarItem === 'settings'
                  ? 'font-semibold text-purple-800'
                  : 'font-normal text-gray-800'
              "
            >
              {{ t('settings') }}
            </div>
          </div>
        </button>
      </div>

      <!-- Right content area -->
      <div class="flex flex-1 flex-col self-stretch overflow-hidden bg-white pb-6">
        <!-- Header -->
        <div class="flex h-14 shrink-0 items-center justify-center gap-6 bg-white p-4">
          <div class="flex flex-1 items-center justify-start gap-2">
            <!-- Back button for create forms -->
            <button
              v-if="showCreateForm || showSettingsCreateForm"
              type="button"
              class="relative h-6 w-6 overflow-hidden text-gray-800 hover:opacity-70"
              @click="showCreateForm ? resetCreateForm() : resetSettingsCreateForm()"
            >
              <ChevronLeft
                class="h-6 w-6"
                :stroke-width="1.25"
              />
            </button>
            <div
              class="line-clamp-1 flex-1 text-base font-medium leading-6 text-gray-800 font-inter"
            >
              <template v-if="isMoveMode">
                "{{ effectiveMoveSessionTitle }}" {{ t('moveHeader') }}
              </template>
              <template v-else-if="isPromptAssignMode">
                "{{ promptToAssign?.title }}" {{ t('assignPromptHeader') }}
              </template>
              <template v-else>
                {{
                  showCreateForm
                    ? editingPrompt
                      ? t('editPrompt')
                      : t('createPromptTemplate')
                    : showSettingsCreateForm
                      ? editingPreference
                        ? t('editSetting')
                        : t('createSetting')
                      : activeSidebarItem === 'folders'
                        ? t('folders')
                        : activeSidebarItem === 'prompts'
                          ? t('prompts')
                          : activeSidebarItem === 'settings'
                            ? t('settings')
                            : t('chats')
                }}
              </template>
            </div>
          </div>
          <button
            type="button"
            class="relative h-6 w-6 overflow-hidden text-gray-800 hover:opacity-70"
            @click="emit('update:open', false)"
          >
            <X
              class="h-6 w-6"
              :stroke-width="1.25"
            />
          </button>
        </div>

        <!-- ========== CHATS VIEW ========== -->
        <template v-if="activeSidebarItem === 'chats'">
          <div class="flex min-h-0 flex-1 flex-col">
            <!-- Tabs + search -->
            <div class="flex shrink-0 items-center gap-2.5 border-b border-gray-100 px-4 pb-5">
              <button
                type="button"
                class="flex items-center justify-center gap-2.5 px-5 py-2.5 text-sm font-medium leading-5 text-black
                  font-inter"
                :class="
                  activeTab === 'mine'
                    ? 'rounded outline outline-1 -outline-offset-1 outline-gray-200'
                    : 'rounded-[300px]'
                "
                @click="activeTab = 'mine'"
              >
                {{ t('mine') }}
              </button>
              <button
                type="button"
                class="flex items-center justify-center gap-2.5 px-5 py-2.5 text-sm font-medium leading-5 text-black
                  font-inter"
                :class="
                  activeTab === 'team'
                    ? 'rounded outline outline-1 -outline-offset-1 outline-gray-200'
                    : 'rounded-[300px]'
                "
                @click="activeTab = 'team'"
              >
                {{ t('team') }}
              </button>
              <button
                type="button"
                class="flex items-center justify-center gap-2.5 px-5 py-2.5 text-sm font-medium leading-5 text-black
                  font-inter"
                :class="
                  activeTab === 'all'
                    ? 'rounded outline outline-1 -outline-offset-1 outline-gray-200'
                    : 'rounded-[300px]'
                "
                @click="activeTab = 'all'"
              >
                {{ t('all') }}
              </button>
              <input
                v-model="searchQuery"
                type="text"
                class="h-10 flex-1 rounded-[30px] border border-gray-200 bg-white px-3 py-2.5 text-sm font-normal leading-5
                  text-gray-800 font-inter placeholder:text-gray-500 focus:ring-0"
                :placeholder="t('search')"
              />
            </div>

            <!-- Session list -->
            <div
              ref="chatListRef"
              class="min-h-0 flex-1 overflow-y-auto"
              @scroll="onChatListScroll"
            >
              <!-- Initial loading -->
              <div
                v-if="loading && filteredSessions.length === 0"
                class="flex h-full items-center justify-center"
              >
                <SpinnerLoader :size="32" />
              </div>

              <template v-else-if="filteredSessions.length > 0">
                <div
                  v-for="session in filteredSessions"
                  :key="session.uuid"
                  class="group inline-flex w-full items-center gap-3 self-stretch border-b border-gray-50 p-4 text-left
                    transition-colors hover:bg-gray-50"
                >
                  <button
                    type="button"
                    class="flex flex-1 items-center gap-3 text-left"
                    @click="handleSelectSession(session.uuid)"
                  >
                    <div
                      class="flex h-10 w-10 min-w-10 items-center justify-center gap-2.5 rounded-[37.5px] bg-gray-50 p-2"
                    >
                      <div
                        class="text-center text-lg font-semibold leading-6 text-gray-500 font-inter"
                      >
                        {{ getInitials(session) }}
                      </div>
                    </div>
                    <div class="inline-flex flex-1 flex-col items-start justify-center">
                      <div
                        class="line-clamp-1 w-28 text-sm font-semibold leading-5 text-gray-800 font-inter"
                      >
                        {{ getUserName(session) }}
                      </div>
                      <div
                        class="line-clamp-1 self-stretch text-sm font-normal leading-5 text-gray-500 font-inter"
                        :title="getSessionSubtitle(session)"
                      >
                        {{ getSessionSubtitle(session) }}
                      </div>
                    </div>
                  </button>
                  <DropdownMenu>
                    <DropdownMenuTrigger as-child>
                      <button
                        type="button"
                        class="flex h-6 w-6 shrink-0 cursor-pointer items-center justify-center rounded text-gray-600 opacity-0
                          transition-opacity group-hover:opacity-100 data-[state=open]:opacity-100"
                        :title="t('moreOptions')"
                        :aria-label="t('moreOptions')"
                        data-cy="chat-row-options-trigger"
                        @click.stop
                      >
                        <MoreHorizontal
                          class="h-6 w-6 fill-current"
                          :stroke-width="1.5"
                        />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                      align="end"
                      :side-offset="4"
                      class="w-[260px] overflow-hidden rounded-xl border-[#E5E7EB] p-0 shadow-[0px_4px_8px_0px_rgba(0,0,0,0.08)]"
                      data-cy="chat-row-options-content"
                    >
                      <DropdownMenuItem
                        class="gap-2 rounded-none border-b border-[#F9FAFB] px-3 py-2.5 text-sm text-[#1F2937]"
                        data-cy="chat-row-options-download-pdf"
                        @click="handleRowDownloadPdf(session)"
                      >
                        <Download class="h-4 w-4 shrink-0" />
                        {{ t('downloadChatAsPdf') }}
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        class="gap-2 rounded-none border-b border-[#F9FAFB] px-3 py-2.5 text-sm text-[#1F2937]"
                        data-cy="chat-row-options-rename"
                        @click="handleRowRename(session)"
                      >
                        <Pencil class="h-4 w-4 shrink-0" />
                        {{ t('renameChat') }}
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        class="gap-2 rounded-none border-b border-[#F9FAFB] px-3 py-2.5 text-sm text-[#1F2937]"
                        data-cy="chat-row-options-organize"
                        @click="startLocalMove(session)"
                      >
                        <FolderSymlink class="h-4 w-4 shrink-0" />
                        {{ t('organizeChat') }}
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        class="gap-2 rounded-none px-3 py-2.5 text-sm text-[#1F2937]"
                        data-cy="chat-row-options-delete"
                        @click="handleRowDelete(session)"
                      >
                        <Trash2 class="h-4 w-4 shrink-0" />
                        {{ t('deleteChat') }}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>

                <!-- Load more spinner -->
                <div
                  v-if="loadingMore"
                  class="flex items-center justify-center py-4"
                >
                  <SpinnerLoader :size="24" />
                </div>
              </template>

              <!-- Empty state -->
              <div
                v-else-if="!loading"
                class="flex h-full flex-col items-center justify-center px-6 text-center"
              >
                <div
                  class="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-gray-100"
                >
                  <Search class="h-6 w-6 text-gray-400" />
                </div>
                <p class="text-base font-medium text-gray-900 font-inter">
                  {{ t('noChatsFound') }}
                </p>
                <p class="mt-1 text-sm text-gray-500 font-inter">
                  {{ emptyStateMessage }}
                </p>
              </div>
            </div>
          </div>
        </template>

        <!-- ========== PROMPTS VIEW ========== -->
        <template v-else-if="activeSidebarItem === 'prompts'">
          <!-- Create form -->
          <template v-if="showCreateForm">
            <div class="flex min-h-0 flex-1 flex-col overflow-y-auto px-4">
              <div class="flex flex-col items-end gap-3 self-stretch">
                <!-- Title -->
                <div class="flex flex-col items-start gap-1 self-stretch">
                  <div class="inline-flex items-start gap-1 self-stretch">
                    <label class="flex-1 text-sm font-semibold leading-5 text-gray-600 font-inter">
                      {{ t('title') }}
                    </label>
                  </div>
                  <input
                    v-model="createTitle"
                    type="text"
                    :maxlength="TITLE_MAX"
                    class="h-10 w-full rounded-lg bg-white px-2.5 py-2 text-base font-normal leading-6 text-gray-800 outline
                      outline-1 -outline-offset-[0.5px] outline-gray-200 font-inter placeholder:text-gray-400
                      focus:shadow-[0px_0px_0px_5px_rgba(147,51,234,1),0px_0px_0px_2px_white] focus:outline-gray-200
                      focus:ring-0"
                  />
                  <div
                    class="self-stretch text-right text-xs font-medium leading-4 text-gray-500 font-inter"
                  >
                    {{ createTitle.length }}/{{ TITLE_MAX }} {{ t('characters') }}
                  </div>
                </div>

                <!-- Law field + Law topic row -->
                <div class="inline-flex items-start gap-5 self-stretch">
                  <!-- Law field -->
                  <div class="inline-flex flex-1 flex-col items-start gap-1">
                    <div
                      class="self-stretch text-base font-medium leading-6 text-gray-800 font-inter"
                    >
                      {{ t('lawField') }}
                    </div>
                    <div class="relative w-full">
                      <button
                        type="button"
                        class="flex h-10 w-full items-center gap-1.5 overflow-hidden rounded-lg bg-white px-2.5 py-2 outline
                          outline-1 -outline-offset-1 outline-gray-200"
                        @click="showCreateLawFieldDropdown = !showCreateLawFieldDropdown"
                      >
                        <span
                          class="flex-1 self-stretch text-left text-base font-normal leading-6 font-inter"
                          :class="createLawField ? 'text-gray-800' : 'text-gray-400'"
                        >
                          {{ createLawField || t('select') }}
                        </span>
                        <ChevronDown
                          class="h-6 w-6 text-gray-800"
                          :stroke-width="1.5"
                        />
                      </button>
                      <div
                        v-if="showCreateLawFieldDropdown"
                        class="absolute left-0 top-full z-10 mt-1 max-h-48 w-full overflow-y-auto rounded-lg bg-white shadow-lg
                          outline outline-1 outline-gray-200"
                      >
                        <button
                          v-for="opt in lawFieldOptions"
                          :key="opt.value"
                          type="button"
                          class="w-full px-3 py-2 text-left text-sm text-gray-800 font-inter hover:bg-gray-50"
                          @click="selectCreateLawField(opt.value)"
                        >
                          {{ opt.title }}
                        </button>
                      </div>
                    </div>
                  </div>
                  <!-- Law topic -->
                  <div class="inline-flex flex-1 flex-col items-start gap-1">
                    <div
                      class="self-stretch text-base font-medium leading-6 text-gray-800 font-inter"
                    >
                      {{ t('lawTopic') }}
                    </div>
                    <div class="relative w-full">
                      <button
                        type="button"
                        class="flex h-10 w-full items-center gap-1.5 overflow-hidden rounded-lg bg-white px-2.5 py-2 outline
                          outline-1 -outline-offset-1 outline-gray-200"
                        :disabled="!createLawField"
                        @click="showCreateLawTopicDropdown = !showCreateLawTopicDropdown"
                      >
                        <span
                          class="flex-1 self-stretch text-left text-base font-normal leading-6 font-inter"
                          :class="createLawTopic ? 'text-gray-800' : 'text-gray-400'"
                        >
                          {{ createLawTopic || t('select') }}
                        </span>
                        <ChevronDown
                          class="h-6 w-6 text-gray-800"
                          :stroke-width="1.5"
                        />
                      </button>
                      <div
                        v-if="showCreateLawTopicDropdown && lawTopicOptions.length > 0"
                        class="absolute left-0 top-full z-10 mt-1 max-h-48 w-full overflow-y-auto rounded-lg bg-white shadow-lg
                          outline outline-1 outline-gray-200"
                      >
                        <button
                          v-for="opt in lawTopicOptions"
                          :key="opt.value"
                          type="button"
                          class="w-full px-3 py-2 text-left text-sm text-gray-800 font-inter hover:bg-gray-50"
                          @click="selectCreateLawTopic(opt.value)"
                        >
                          {{ opt.title }}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Prompt -->
                <div class="flex flex-col items-start gap-1 self-stretch">
                  <div
                    class="self-stretch text-base font-medium leading-6 text-gray-800 font-inter"
                  >
                    {{ t('prompt') }}
                  </div>
                  <textarea
                    v-model="createPrompt"
                    class="h-24 w-full resize-none rounded-lg bg-white px-2.5 py-2 text-base font-normal leading-6
                      text-gray-800 outline outline-1 -outline-offset-1 outline-gray-200 font-inter
                      placeholder:text-gray-400 focus:ring-0"
                    :placeholder="t('promptPlaceholder')"
                  />
                </div>

                <!-- Generate with AI button -->
                <button
                  type="button"
                  class="inline-flex items-center overflow-hidden rounded bg-purple-100 px-1 py-0.5 outline outline-1
                    -outline-offset-1 outline-purple-200"
                  :disabled="!createPrompt.trim() || createGeneratingDesc"
                  @click="handleGenerateDescription"
                >
                  <RotateCcw
                    class="h-4 w-4 text-purple-700"
                    :class="createGeneratingDesc ? 'animate-spin' : ''"
                    :stroke-width="1.5"
                  />
                  <div class="flex items-center justify-center px-1">
                    <span class="text-xs font-medium leading-4 text-purple-700 font-inter">
                      {{ t('generateWithAi') }}
                    </span>
                  </div>
                </button>

                <!-- Description -->
                <div class="flex flex-col items-start gap-1 self-stretch">
                  <div
                    class="self-stretch text-base font-medium leading-6 text-gray-800 font-inter"
                  >
                    {{ t('description') }}
                  </div>
                  <textarea
                    v-model="createDescription"
                    :maxlength="DESC_MAX"
                    class="h-20 w-full resize-none rounded-lg bg-white px-2.5 py-2 text-base font-normal leading-6
                      text-gray-800 outline outline-1 -outline-offset-1 outline-gray-200 font-inter
                      placeholder:text-gray-400 focus:ring-0"
                    :placeholder="t('descriptionPlaceholder')"
                  />
                  <div
                    class="self-stretch text-right text-xs font-medium leading-4 text-gray-500 font-inter"
                  >
                    {{ createDescription.length }}/{{ DESC_MAX }} {{ t('characters') }}
                  </div>
                </div>

                <!-- Folder assignment -->
                <div class="flex flex-col items-start gap-1 self-stretch">
                  <div
                    class="self-stretch text-base font-medium leading-6 text-gray-800 font-inter"
                  >
                    {{ t('assignToFolders') }}
                  </div>
                  <div
                    v-if="folders.length > 0"
                    class="flex flex-wrap gap-2"
                  >
                    <label
                      v-for="folder in folders"
                      :key="folder.uuid"
                      class="flex cursor-pointer items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium outline
                        outline-1 -outline-offset-1 transition-colors"
                      :class="
                        createFolderUuids.includes(folder.uuid)
                          ? 'bg-purple-100 text-purple-700 outline-purple-300'
                          : 'bg-white text-gray-600 outline-gray-200 hover:bg-gray-50'
                      "
                    >
                      <input
                        type="checkbox"
                        class="sr-only"
                        :value="folder.uuid"
                        :checked="createFolderUuids.includes(folder.uuid)"
                        @change="
                          createFolderUuids.includes(folder.uuid)
                            ? (createFolderUuids = createFolderUuids.filter(
                                (u) => u !== folder.uuid
                              ))
                            : createFolderUuids.push(folder.uuid)
                        "
                      />
                      {{ folder.name }}
                    </label>
                  </div>
                  <p
                    v-else
                    class="text-xs text-gray-400"
                  >
                    {{ t('noFoldersFound') }}
                  </p>
                </div>

                <!-- Actions -->
                <div class="inline-flex items-start justify-end gap-4 self-stretch">
                  <button
                    type="button"
                    class="flex h-10 items-center justify-center overflow-hidden rounded-lg bg-white p-2 outline outline-1
                      -outline-offset-1 outline-gray-200"
                    @click="resetCreateForm"
                  >
                    <div class="flex items-center justify-center gap-2 px-2">
                      <span class="text-base font-medium leading-6 text-gray-800 font-inter">
                        {{ t('cancel') }}
                      </span>
                    </div>
                  </button>
                  <!-- Use prompt button (edit mode only) -->
                  <button
                    v-if="editingPrompt"
                    type="button"
                    class="flex h-10 items-center justify-center overflow-hidden rounded-lg bg-white p-2 outline outline-1
                      -outline-offset-1 outline-purple-300"
                    :disabled="!createPrompt.trim()"
                    :class="!createPrompt.trim() ? 'opacity-50' : ''"
                    @click="handleUsePromptFromForm"
                  >
                    <div class="flex items-center justify-center gap-2 px-2">
                      <span class="text-base font-medium leading-6 text-purple-700 font-inter">
                        {{ t('usePrompt') }}
                      </span>
                    </div>
                  </button>
                  <!-- Save / Create button -->
                  <button
                    type="button"
                    class="flex h-10 items-center justify-center overflow-hidden rounded-lg bg-primary p-2 shadow-sm"
                    :disabled="!canCreate"
                    :class="!canCreate ? 'opacity-50' : ''"
                    @click="handleCreate"
                  >
                    <div class="flex items-center justify-center gap-2 px-2">
                      <span class="text-base font-medium leading-6 text-white font-inter">
                        {{ editingPrompt ? t('saveTemplate') : t('createTemplate') }}
                      </span>
                    </div>
                  </button>
                </div>
              </div>
            </div>
          </template>

          <!-- Prompt list -->
          <template v-else>
            <div class="flex min-h-0 flex-1 flex-col">
              <!-- Search + law field filter + create -->
              <div class="flex shrink-0 items-center gap-2.5 border-b border-gray-100 px-4 pb-5">
                <input
                  v-model="promptSearchQuery"
                  type="text"
                  class="h-10 flex-1 rounded-[30px] border border-gray-200 bg-white px-3 py-2.5 text-sm font-normal leading-5
                    text-gray-800 font-inter placeholder:text-gray-500 focus:ring-0"
                  :placeholder="t('search')"
                />
                <!-- Law field filter dropdown -->
                <div class="relative">
                  <button
                    type="button"
                    class="flex h-8 items-center justify-center gap-1.5 overflow-hidden rounded-md bg-white p-1.5 px-3 text-sm
                      font-medium leading-5 text-gray-800 shadow-sm outline outline-1 -outline-offset-1 outline-gray-200
                      font-inter"
                    @click="showLawFieldDropdown = !showLawFieldDropdown"
                  >
                    <span>{{ promptLawFieldFilter || t('selectLawField') }}</span>
                    <ChevronDown
                      class="h-5 w-5 text-gray-800"
                      :stroke-width="1.5"
                    />
                  </button>
                  <div
                    v-if="showLawFieldDropdown"
                    class="absolute right-0 top-full z-10 mt-1 max-h-60 w-56 overflow-y-auto rounded-lg bg-white shadow-lg
                      outline outline-1 outline-gray-200"
                  >
                    <button
                      type="button"
                      class="w-full px-3 py-2 text-left text-sm text-gray-600 font-inter hover:bg-gray-50"
                      @click="selectPromptLawFieldFilter(null)"
                    >
                      {{ t('all') }}
                    </button>
                    <button
                      v-for="field in availableLawFields"
                      :key="field"
                      type="button"
                      class="w-full px-3 py-2 text-left text-sm text-gray-800 font-inter hover:bg-gray-50"
                      :class="promptLawFieldFilter === field ? 'bg-gray-50 font-medium' : ''"
                      @click="selectPromptLawFieldFilter(field)"
                    >
                      {{ field }}
                    </button>
                  </div>
                </div>
                <!-- Create button -->
                <button
                  type="button"
                  class="flex items-center justify-center gap-2 rounded-[200px] bg-primary px-4 py-2"
                  @click="showCreateForm = true"
                >
                  <Plus
                    class="h-6 w-6 text-white"
                    :stroke-width="1.5"
                  />
                </button>
              </div>

              <!-- Prompt list -->
              <div class="min-h-0 flex-1 overflow-y-auto">
                <!-- Initial loading -->
                <div
                  v-if="promptsLoading && filteredPrompts.length === 0"
                  class="flex h-full items-center justify-center"
                >
                  <SpinnerLoader :size="32" />
                </div>

                <template v-else-if="filteredPrompts.length > 0">
                  <div
                    v-for="prompt in filteredPrompts"
                    :key="prompt.uuid"
                    class="group flex w-full items-center gap-3 self-stretch border-b border-gray-50 p-4 text-left
                      transition-colors hover:bg-gray-50"
                  >
                    <div
                      class="inline-flex min-w-0 flex-1 cursor-pointer flex-col items-start justify-center gap-2.5"
                      role="button"
                      tabindex="0"
                      @click="handleUsePrompt(prompt)"
                      @keydown.enter.prevent="handleUsePrompt(prompt)"
                      @keydown.space.prevent="handleUsePrompt(prompt)"
                    >
                      <div class="flex flex-col items-start justify-center self-stretch">
                        <div
                          class="line-clamp-1 self-stretch text-sm font-semibold leading-5 text-gray-800 font-inter"
                        >
                          {{ prompt.title }}
                        </div>
                        <div
                          class="line-clamp-1 self-stretch text-sm font-normal leading-5 text-gray-500 font-inter"
                        >
                          {{ prompt.description || prompt.prompt }}
                        </div>
                      </div>
                      <!-- Tags -->
                      <div class="inline-flex items-start gap-2">
                        <div
                          v-if="prompt.is_jupus_template"
                          class="flex min-w-5 items-center justify-center gap-2 rounded-3xl bg-purple-200 px-1.5 py-0.5"
                        >
                          <span class="text-xs font-semibold leading-4 text-purple-700 font-inter">
                            JUPUS
                          </span>
                        </div>
                        <div
                          v-if="prompt.law_field"
                          class="flex items-start overflow-hidden rounded-full bg-white px-1 py-0.5 outline outline-1
                            -outline-offset-1 outline-gray-200"
                        >
                          <div class="flex items-center justify-center px-1">
                            <span class="text-xs font-medium leading-4 text-gray-800 font-inter">
                              {{
                                getLawFieldLabel(
                                  prompt.law_field as string,
                                  prompt.law_topic as string | null
                                )
                              }}
                            </span>
                          </div>
                        </div>
                        <!-- Star inline with tags -->
                        <button
                          type="button"
                          class="relative h-5 w-5 shrink-0 overflow-hidden"
                          :class="
                            prompt.is_favorite === 'true'
                              ? 'opacity-100'
                              : 'opacity-0 group-hover:opacity-100'
                          "
                          @click.stop="toggleFavorite(prompt, $event)"
                        >
                          <Star
                            class="absolute left-[1.67px] top-[1.67px] h-4 w-4"
                            :class="
                              prompt.is_favorite === 'true'
                                ? 'fill-purple-500 text-purple-500'
                                : 'text-gray-400 hover:fill-purple-500 hover:text-purple-500'
                            "
                            :stroke-width="1.25"
                          />
                        </button>
                      </div>
                    </div>
                    <!-- Per-row action menu — kebab matches folder-row trigger styling -->
                    <DropdownMenu>
                      <DropdownMenuTrigger as-child>
                        <button
                          type="button"
                          class="flex h-6 w-6 shrink-0 cursor-pointer items-center justify-center rounded text-gray-600 opacity-0
                            transition-opacity group-hover:opacity-100 data-[state=open]:opacity-100"
                          :title="t('moreOptions')"
                          :aria-label="t('moreOptions')"
                          data-cy="prompt-row-options-trigger"
                          @click.stop
                        >
                          <MoreHorizontal
                            class="h-6 w-6 fill-current"
                            :stroke-width="1.5"
                          />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent
                        align="end"
                        :side-offset="4"
                        class="w-[220px] overflow-hidden rounded-xl border-[#E5E7EB] p-0 shadow-[0px_4px_8px_0px_rgba(0,0,0,0.08)]"
                        data-cy="prompt-row-options-content"
                      >
                        <DropdownMenuItem
                          v-if="!prompt.is_jupus_template"
                          class="gap-2 rounded-none border-b border-[#F9FAFB] px-3 py-2.5 text-sm text-[#1F2937]"
                          data-cy="prompt-row-options-edit"
                          @click="openEditForm(prompt)"
                        >
                          <Pencil class="h-4 w-4 shrink-0" />
                          {{ t('promptMenuEdit') }}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          :class="[
                            'gap-2 rounded-none px-3 py-2.5 text-sm text-[#1F2937]',
                            !prompt.is_jupus_template ? 'border-b border-[#F9FAFB]' : ''
                          ]"
                          data-cy="prompt-row-options-duplicate"
                          @click="handleCopyPrompt(prompt)"
                        >
                          <Copy class="h-4 w-4 shrink-0" />
                          {{ t('promptMenuDuplicate') }}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          v-if="!prompt.is_jupus_template"
                          class="gap-2 rounded-none px-3 py-2.5 text-sm text-red-600"
                          data-cy="prompt-row-options-delete"
                          @click="handlePromptRowDelete(prompt)"
                        >
                          <Trash2 class="h-4 w-4 shrink-0" />
                          {{ t('promptMenuDelete') }}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </template>

                <!-- Empty state -->
                <div
                  v-else-if="!promptsLoading"
                  class="flex h-full flex-col items-center justify-center px-6 text-center"
                >
                  <div
                    class="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-gray-100"
                  >
                    <ListRestart class="h-6 w-6 text-gray-400" />
                  </div>
                  <p class="text-base font-medium text-gray-900 font-inter">
                    {{ t('noPromptsFound') }}
                  </p>
                  <p class="mt-1 text-sm text-gray-500 font-inter">
                    {{ promptEmptyMessage }}
                  </p>
                </div>
              </div>
            </div>
          </template>
        </template>

        <!-- ========== FOLDERS VIEW ========== -->
        <template v-else-if="activeSidebarItem === 'folders'">
          <!-- Move/assign mode: flat selectable folder list -->
          <template v-if="isMoveMode || isPromptAssignMode">
            <div class="flex min-h-0 flex-1 flex-col">
              <!-- Search + create button -->
              <div class="flex shrink-0 items-start gap-2.5 border-b border-gray-100 px-4 pb-5">
                <input
                  v-model="folderSearchQuery"
                  type="text"
                  class="h-10 flex-1 rounded-[30px] border border-gray-200 bg-white px-3 py-2.5 text-sm font-normal leading-5
                    text-gray-800 font-inter placeholder:text-gray-500 focus:ring-0"
                  :placeholder="t('search')"
                />
                <button
                  type="button"
                  class="flex items-center justify-center gap-2 rounded-[200px] bg-primary px-4 py-2"
                  @click="openCreateFolderDialog"
                >
                  <FolderPlus
                    class="h-6 w-6 text-white"
                    :stroke-width="1.5"
                  />
                </button>
              </div>

              <!-- Selectable folder list -->
              <div
                ref="folderListRef"
                class="min-h-0 flex-1 overflow-y-auto"
                @scroll="onFolderListScroll"
              >
                <div
                  v-if="foldersLoading && filteredFolders.length === 0"
                  class="flex h-full items-center justify-center"
                >
                  <SpinnerLoader :size="32" />
                </div>

                <template v-else-if="filteredFolders.length > 0">
                  <button
                    v-for="folder in filteredFolders"
                    :key="folder.uuid"
                    type="button"
                    class="inline-flex w-full items-center gap-3 self-stretch border-b border-gray-50 p-4 text-left
                      transition-colors"
                    :class="
                      selectedFolderUuid === folder.uuid ? 'bg-purple-50' : 'hover:bg-gray-50'
                    "
                    @click="selectedFolderUuid = folder.uuid"
                  >
                    <FolderClosed
                      class="h-6 w-6 text-gray-800"
                      :stroke-width="1.5"
                    />
                    <div class="flex flex-1 items-center justify-start gap-3">
                      <div
                        class="line-clamp-1 text-sm font-medium leading-5 text-gray-800 font-inter"
                      >
                        {{ folder.name }}
                      </div>
                      <span
                        v-if="folder.is_default"
                        class="shrink-0 rounded-full bg-purple-100 px-2 py-0.5 text-xs font-medium text-purple-700"
                      >
                        {{ t('defaultFolder') }}
                      </span>
                      <div
                        class="flex min-w-5 items-center justify-center rounded-[20px] bg-gray-100 px-1 py-0.5"
                      >
                        <div class="text-xs font-semibold leading-4 text-gray-800 font-inter">
                          {{ folder.chat_sessions_count }}
                        </div>
                      </div>
                    </div>
                  </button>

                  <div
                    v-if="foldersLoadingMore"
                    class="flex items-center justify-center py-4"
                  >
                    <SpinnerLoader :size="24" />
                  </div>
                </template>

                <div
                  v-else-if="!foldersLoading"
                  class="flex h-full flex-col items-center justify-center px-6 text-center"
                >
                  <div
                    class="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-gray-100"
                  >
                    <Folder class="h-6 w-6 text-gray-400" />
                  </div>
                  <p class="text-base font-medium text-gray-900 font-inter">
                    {{ t('noFoldersFound') }}
                  </p>
                  <p class="mt-1 text-sm text-gray-500 font-inter">
                    {{ folderEmptyMessage }}
                  </p>
                </div>
              </div>

              <!-- Move / assign footer -->
              <div
                class="flex shrink-0 items-center justify-end gap-4 border-t border-gray-100 px-4 py-4"
              >
                <button
                  type="button"
                  class="flex h-10 items-center justify-center rounded-lg px-4 text-base font-medium leading-6 text-gray-800
                    outline outline-1 -outline-offset-1 outline-gray-200 font-inter hover:bg-gray-50"
                  @click="
                    isPromptAssignMode
                      ? cancelPromptAssign()
                      : localMoveSessionUuid
                        ? cancelLocalMove()
                        : emit('update:open', false)
                  "
                >
                  {{ t('cancel') }}
                </button>
                <button
                  v-if="isPromptAssignMode"
                  type="button"
                  class="flex h-10 items-center justify-center rounded-lg bg-primary px-4 text-base font-medium leading-6
                    text-white shadow-sm font-inter"
                  :disabled="!selectedFolderUuid"
                  :class="!selectedFolderUuid ? 'opacity-50' : 'hover:bg-primary/90'"
                  @click="handleAssignPromptToFolder"
                >
                  {{ t('assignPromptToFolder') }}
                </button>
                <button
                  v-else
                  type="button"
                  class="flex h-10 items-center justify-center rounded-lg bg-primary px-4 text-base font-medium leading-6
                    text-white shadow-sm font-inter"
                  :disabled="!selectedFolderUuid"
                  :class="!selectedFolderUuid ? 'opacity-50' : 'hover:bg-primary/90'"
                  @click="handleMoveToFolder"
                >
                  {{ t('moveToFolder') }}
                </button>
              </div>
            </div>
          </template>

          <!-- Normal mode: expandable folder tree -->
          <template v-else>
            <div class="flex min-h-0 flex-1 flex-col">
              <!-- Search + create button -->
              <div class="flex shrink-0 items-start gap-2.5 border-b border-gray-100 px-4 pb-5">
                <input
                  v-model="folderSearchQuery"
                  type="text"
                  class="h-10 flex-1 rounded-[30px] border border-gray-200 bg-white px-3 py-2.5 text-sm font-normal leading-5
                    text-gray-800 font-inter placeholder:text-gray-500 focus:ring-0"
                  :placeholder="t('search')"
                />
                <button
                  type="button"
                  class="flex items-center justify-center gap-2 rounded-[200px] bg-primary px-4 py-2"
                  @click="openCreateFolderDialog"
                >
                  <FolderPlus
                    class="h-6 w-6 text-white"
                    :stroke-width="1.5"
                  />
                </button>
              </div>

              <!-- Folder list -->
              <div
                ref="folderListRef"
                class="min-h-0 flex-1 overflow-y-auto"
                @scroll="onFolderListScroll"
              >
                <!-- Initial loading -->
                <div
                  v-if="foldersLoading && filteredFolders.length === 0"
                  class="flex h-full items-center justify-center"
                >
                  <SpinnerLoader :size="32" />
                </div>

                <template v-else-if="filteredFolders.length > 0">
                  <template
                    v-for="folder in filteredFolders"
                    :key="folder.uuid"
                  >
                    <!-- Folder row -->
                    <div
                      class="group inline-flex w-full items-center gap-3 self-stretch border-b border-gray-50 p-4
                        transition-colors hover:bg-gray-50"
                      :class="expandedFolders.has(folder.uuid) ? 'bg-gray-50' : ''"
                    >
                      <button
                        type="button"
                        class="flex flex-1 items-center gap-3 text-left"
                        @click="toggleFolder(folder.uuid)"
                      >
                        <div class="relative h-6 w-6 overflow-hidden">
                          <FolderClosed
                            class="h-6 w-6 text-gray-800"
                            :stroke-width="1.5"
                          />
                        </div>
                        <div class="flex flex-1 items-center justify-start gap-3">
                          <div
                            class="line-clamp-1 text-sm font-medium leading-5 text-gray-800 font-inter"
                          >
                            {{ folder.name }}
                          </div>
                          <span
                            v-if="folder.is_default"
                            class="shrink-0 rounded-full bg-purple-100 px-2 py-0.5 text-xs font-medium text-purple-700"
                          >
                            {{ t('defaultFolder') }}
                          </span>
                          <div
                            class="flex min-w-5 items-center justify-center rounded-[20px] bg-gray-100 px-1 py-0.5"
                          >
                            <div class="text-xs font-semibold leading-4 text-gray-800 font-inter">
                              {{
                                (folder.chat_sessions_count ?? 0) +
                                (folder.question_templates_count ?? 0)
                              }}
                            </div>
                          </div>
                        </div>
                      </button>
                      <DropdownMenu v-if="!folder.is_default">
                        <DropdownMenuTrigger as-child>
                          <button
                            type="button"
                            class="flex h-6 w-6 shrink-0 cursor-pointer items-center justify-center rounded text-gray-600 opacity-0
                              transition-opacity group-hover:opacity-100 data-[state=open]:opacity-100"
                            :title="t('moreOptions')"
                            :aria-label="t('moreOptions')"
                            data-cy="folder-row-options-trigger"
                            @click.stop
                          >
                            <MoreHorizontal
                              class="h-6 w-6 fill-current"
                              :stroke-width="1.5"
                            />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                          align="end"
                          :side-offset="4"
                          class="w-[220px] overflow-hidden rounded-xl border-[#E5E7EB] p-0 shadow-[0px_4px_8px_0px_rgba(0,0,0,0.08)]"
                          data-cy="folder-row-options-content"
                        >
                          <DropdownMenuItem
                            class="gap-2 rounded-none border-b border-[#F9FAFB] px-3 py-2.5 text-sm text-[#1F2937]"
                            data-cy="folder-row-options-rename"
                            @click="handleFolderRowRename(folder)"
                          >
                            <Pencil class="h-4 w-4 shrink-0" />
                            {{ t('renameFolder') }}
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            class="gap-2 rounded-none px-3 py-2.5 text-sm text-red-600"
                            data-cy="folder-row-options-delete"
                            @click="handleFolderRowDelete(folder)"
                          >
                            <Trash2 class="h-4 w-4 shrink-0" />
                            {{ t('deleteFolder') }}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>

                    <!-- Expanded folder contents (chats + prompts) -->
                    <template v-if="expandedFolders.has(folder.uuid)">
                      <template
                        v-for="item in folderSessionsMap[folder.uuid] ?? []"
                        :key="item.uuid"
                      >
                        <!-- Chat item -->
                        <button
                          v-if="item.type === 'chat'"
                          type="button"
                          class="inline-flex w-full items-center gap-3 self-stretch border-b border-gray-50 bg-gray-50 p-4 pl-8
                            text-left transition-colors hover:bg-gray-100"
                          @click="handleSelectSession(item.uuid)"
                        >
                          <div
                            class="flex h-10 w-10 min-w-10 items-center justify-center gap-2.5 rounded-[37.5px] bg-gray-200 p-2"
                          >
                            <div
                              class="text-center text-lg font-semibold leading-6 text-gray-500 font-inter"
                            >
                              {{ getInitials(item) }}
                            </div>
                          </div>
                          <div class="inline-flex flex-1 flex-col items-start justify-center">
                            <div
                              class="line-clamp-1 w-28 text-sm font-semibold leading-5 text-gray-800 font-inter"
                            >
                              {{ getUserName(item) }}
                            </div>
                            <div
                              class="line-clamp-1 self-stretch text-sm font-normal leading-5 text-gray-500 font-inter"
                              :title="getSessionSubtitle(item)"
                            >
                              {{ getSessionSubtitle(item) }}
                            </div>
                          </div>
                          <span
                            class="shrink-0 rounded-full bg-gray-200 px-2 py-0.5 text-xs font-medium text-gray-600"
                          >
                            {{ t('chatBadge') }}
                          </span>
                        </button>
                        <!-- Prompt item -->
                        <button
                          v-else-if="item.type === 'prompt'"
                          type="button"
                          class="inline-flex w-full items-center gap-3 self-stretch border-b border-gray-50 bg-purple-50 p-4 pl-8
                            text-left hover:bg-purple-100"
                          @click="openPromptEditFromFolder(item)"
                        >
                          <div
                            class="flex h-10 w-10 min-w-10 items-center justify-center gap-2.5 rounded-[37.5px] bg-purple-200 p-2"
                          >
                            <div class="text-xs font-semibold text-purple-700 font-inter">P</div>
                          </div>
                          <div class="inline-flex flex-1 flex-col items-start justify-center">
                            <div
                              class="line-clamp-1 self-stretch text-sm font-semibold leading-5 text-gray-800 font-inter"
                            >
                              {{ item.title }}
                            </div>
                            <div
                              class="line-clamp-1 self-stretch text-sm font-normal leading-5 text-gray-500 font-inter"
                            >
                              {{ item.description || item.prompt }}
                            </div>
                          </div>
                          <span
                            class="shrink-0 rounded-full bg-purple-200 px-2 py-0.5 text-xs font-medium text-purple-700"
                          >
                            {{ t('promptBadge') }}
                          </span>
                        </button>
                      </template>
                    </template>
                  </template>

                  <!-- Load more spinner -->
                  <div
                    v-if="foldersLoadingMore"
                    class="flex items-center justify-center py-4"
                  >
                    <SpinnerLoader :size="24" />
                  </div>
                </template>

                <!-- Folders empty state -->
                <div
                  v-else-if="!foldersLoading"
                  class="flex h-full flex-col items-center justify-center px-6 text-center"
                >
                  <div
                    class="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-gray-100"
                  >
                    <Folder class="h-6 w-6 text-gray-400" />
                  </div>
                  <p class="text-base font-medium text-gray-900 font-inter">
                    {{ t('noFoldersFound') }}
                  </p>
                  <p class="mt-1 text-sm text-gray-500 font-inter">
                    {{ folderEmptyMessage }}
                  </p>
                </div>
              </div>
            </div>
          </template>
        </template>

        <!-- ========== SETTINGS VIEW ========== -->
        <template v-else-if="activeSidebarItem === 'settings'">
          <!-- Create form -->
          <template v-if="showSettingsCreateForm">
            <div class="flex min-h-0 flex-1 flex-col overflow-y-auto px-4">
              <div class="flex flex-col items-end gap-3 self-stretch">
                <!-- Title -->
                <div class="flex flex-col items-start gap-1 self-stretch">
                  <div class="inline-flex items-start gap-1 self-stretch">
                    <label class="flex-1 text-sm font-semibold leading-5 text-gray-600 font-inter">
                      {{ t('title') }}
                    </label>
                  </div>
                  <input
                    v-model="settingsCreateTitle"
                    type="text"
                    :maxlength="SETTINGS_TITLE_MAX"
                    class="h-10 w-full rounded-lg bg-white px-2.5 py-2 text-base font-normal leading-6 text-gray-800 outline
                      outline-1 -outline-offset-[0.5px] outline-gray-200 font-inter placeholder:text-gray-400
                      focus:shadow-[0px_0px_0px_5px_rgba(147,51,234,1),0px_0px_0px_2px_white] focus:outline-gray-200
                      focus:ring-0"
                  />
                  <div
                    class="self-stretch text-right text-xs font-medium leading-4 text-gray-500 font-inter"
                  >
                    {{ settingsCreateTitle.length }}/{{ SETTINGS_TITLE_MAX }} {{ t('characters') }}
                  </div>
                </div>

                <!-- Visibility -->
                <div class="inline-flex items-start gap-5 self-stretch">
                  <div class="inline-flex flex-1 flex-col items-start gap-1">
                    <div
                      class="self-stretch text-base font-medium leading-6 text-gray-800 font-inter"
                    >
                      {{ t('visibility') }}
                    </div>
                    <div class="relative w-full">
                      <button
                        type="button"
                        class="flex h-10 w-full items-center gap-1.5 overflow-hidden rounded-lg bg-white px-2.5 py-2 outline
                          outline-1 -outline-offset-1 outline-gray-200"
                        @click="showSettingsScopeDropdown = !showSettingsScopeDropdown"
                      >
                        <span
                          class="flex-1 self-stretch text-left text-base font-normal leading-6 font-inter"
                          :class="settingsCreateScope ? 'text-gray-800' : 'text-gray-400'"
                        >
                          {{
                            settingsCreateScope
                              ? settingsCreateScope === 'ORGANIZATION'
                                ? t('sharedPreference')
                                : t('personalPreference')
                              : t('select')
                          }}
                        </span>
                        <ChevronDown
                          class="h-6 w-6 text-gray-800"
                          :stroke-width="1.5"
                        />
                      </button>
                      <div
                        v-if="showSettingsScopeDropdown"
                        class="absolute left-0 top-full z-10 mt-1 w-full rounded-lg bg-white shadow-lg outline outline-1
                          outline-gray-200"
                      >
                        <button
                          type="button"
                          class="w-full px-3 py-2 text-left text-sm text-gray-800 font-inter hover:bg-gray-50"
                          @click="selectSettingsCreateScope('ORGANIZATION')"
                        >
                          {{ t('sharedPreference') }}
                        </button>
                        <button
                          type="button"
                          class="w-full px-3 py-2 text-left text-sm text-gray-800 font-inter hover:bg-gray-50"
                          @click="selectSettingsCreateScope('USER')"
                        >
                          {{ t('personalPreference') }}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Content -->
                <div class="flex h-64 flex-col items-start gap-1 self-stretch">
                  <div
                    class="self-stretch text-base font-medium leading-6 text-gray-800 font-inter"
                  >
                    {{ t('content') }}
                  </div>
                  <textarea
                    v-model="settingsCreateContent"
                    :maxlength="SETTINGS_CONTENT_MAX"
                    class="w-full flex-1 resize-none rounded-lg bg-white px-2.5 py-2 text-base font-normal leading-6
                      text-gray-800 outline outline-1 -outline-offset-1 outline-gray-200 font-inter
                      placeholder:text-gray-400 focus:ring-0"
                    :placeholder="t('contentPlaceholder')"
                  />
                  <div
                    class="self-stretch text-right text-xs font-medium leading-4 text-gray-500 font-inter"
                  >
                    {{ settingsCreateContent.length }}/{{ SETTINGS_CONTENT_MAX }}
                    {{ t('characters') }}
                  </div>
                </div>

                <!-- Actions -->
                <div class="inline-flex items-start justify-end gap-4 self-stretch">
                  <button
                    type="button"
                    class="flex h-10 items-center justify-center overflow-hidden rounded-lg bg-white p-2 outline outline-1
                      -outline-offset-1 outline-gray-200"
                    @click="resetSettingsCreateForm"
                  >
                    <div class="flex items-center justify-center gap-2 px-2">
                      <span class="text-base font-medium leading-6 text-gray-800 font-inter">
                        {{ t('cancel') }}
                      </span>
                    </div>
                  </button>
                  <button
                    type="button"
                    class="flex h-10 items-center justify-center overflow-hidden rounded-lg bg-primary p-2 shadow-sm"
                    :disabled="!canCreateSetting"
                    :class="!canCreateSetting ? 'opacity-50' : ''"
                    @click="handleCreateSetting"
                  >
                    <div class="flex items-center justify-center gap-2 px-2">
                      <span class="text-base font-medium leading-6 text-white font-inter">
                        {{ editingPreference ? t('saveSetting') : t('createSettingBtn') }}
                      </span>
                    </div>
                  </button>
                </div>
              </div>
            </div>
          </template>

          <!-- Settings list -->
          <template v-else>
            <div class="flex min-h-0 flex-1 flex-col gap-2">
              <!-- Search + filter + create -->
              <div class="flex shrink-0 items-center gap-2.5 border-b border-gray-100 px-4 pb-5">
                <input
                  v-model="settingsSearchQuery"
                  type="text"
                  class="h-10 flex-1 rounded-[30px] border border-gray-200 bg-white px-3 py-2.5 text-sm font-normal leading-5
                    text-gray-800 font-inter placeholder:text-gray-500 focus:ring-0"
                  :placeholder="t('search')"
                />
                <!-- Filter dropdown -->
                <div class="relative">
                  <button
                    type="button"
                    class="flex h-8 items-center justify-center gap-1.5 overflow-hidden rounded-md bg-white p-1.5 px-3 text-sm
                      font-medium leading-5 text-gray-800 shadow-sm outline outline-1 -outline-offset-1 outline-gray-200
                      font-inter"
                    @click="showSettingsFilterDropdown = !showSettingsFilterDropdown"
                  >
                    <span>{{ settingsFilterLabel }}</span>
                    <ChevronDown
                      class="h-5 w-5 text-gray-800"
                      :stroke-width="1.5"
                    />
                  </button>
                  <div
                    v-if="showSettingsFilterDropdown"
                    class="absolute right-0 top-full z-10 mt-1 w-48 rounded-lg bg-white shadow-lg outline outline-1
                      outline-gray-200"
                  >
                    <button
                      type="button"
                      class="w-full px-3 py-2 text-left text-sm text-gray-800 font-inter hover:bg-gray-50"
                      :class="settingsFilter === 'all' ? 'bg-gray-50 font-medium' : ''"
                      @click="selectSettingsFilter('all')"
                    >
                      {{ t('allSettings') }}
                    </button>
                    <button
                      type="button"
                      class="w-full px-3 py-2 text-left text-sm text-gray-800 font-inter hover:bg-gray-50"
                      :class="settingsFilter === 'shared' ? 'bg-gray-50 font-medium' : ''"
                      @click="selectSettingsFilter('shared')"
                    >
                      {{ t('shared') }}
                    </button>
                    <button
                      type="button"
                      class="w-full px-3 py-2 text-left text-sm text-gray-800 font-inter hover:bg-gray-50"
                      :class="settingsFilter === 'personal' ? 'bg-gray-50 font-medium' : ''"
                      @click="selectSettingsFilter('personal')"
                    >
                      {{ t('personal') }}
                    </button>
                  </div>
                </div>
                <!-- Create button -->
                <button
                  type="button"
                  class="flex items-center justify-center gap-2 rounded-[200px] bg-primary px-4 py-2"
                  @click="showSettingsCreateForm = true"
                >
                  <Plus
                    class="h-6 w-6 text-white"
                    :stroke-width="1.5"
                  />
                </button>
              </div>

              <!-- Preference list -->
              <div class="min-h-0 flex-1 overflow-y-auto">
                <!-- Loading -->
                <div
                  v-if="preferencesLoading && filteredPreferences.length === 0"
                  class="flex h-full items-center justify-center"
                >
                  <SpinnerLoader :size="32" />
                </div>

                <template v-else-if="filteredPreferences.length > 0">
                  <div
                    v-for="pref in filteredPreferences"
                    :key="pref.uuid"
                    class="group inline-flex w-full items-center gap-7 self-stretch border-b border-gray-50 p-4"
                  >
                    <!-- Content -->
                    <div class="inline-flex flex-1 flex-col items-start justify-center gap-2.5">
                      <div class="flex flex-col items-start justify-center self-stretch">
                        <div
                          class="line-clamp-1 self-stretch text-sm font-semibold leading-5 text-gray-800 font-inter"
                        >
                          {{ pref.title }}
                        </div>
                        <div
                          class="line-clamp-1 self-stretch text-sm font-normal leading-5 text-gray-500 font-inter"
                        >
                          {{ pref.content }}
                        </div>
                      </div>
                      <!-- Tags -->
                      <div class="inline-flex items-start gap-2">
                        <div
                          class="flex items-start overflow-hidden rounded-full bg-gray-100 px-1 py-0.5 outline outline-1
                            -outline-offset-1 outline-gray-200"
                        >
                          <div class="flex items-center justify-center px-1">
                            <span class="text-xs font-medium leading-4 text-gray-700 font-inter">
                              {{ getScopeLabel(pref.scope) }}
                            </span>
                          </div>
                        </div>
                        <div
                          v-if="pref.created_by_user_name"
                          class="flex items-start overflow-hidden rounded-full bg-white px-1 py-0.5 outline outline-1
                            -outline-offset-1 outline-gray-200"
                        >
                          <div class="flex items-center justify-center px-1">
                            <span class="text-xs font-medium leading-4 text-gray-800 font-inter">
                              {{ pref.created_by_user_name }}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Toggle switch -->
                    <button
                      type="button"
                      class="flex items-start overflow-hidden rounded-[999px] p-0.5"
                      :class="pref.is_active ? 'bg-primary' : 'bg-gray-200'"
                      @click="togglePreferenceActive(pref, $event)"
                    >
                      <div
                        class="flex h-5 w-5 items-center justify-center rounded-[999px]"
                        :class="pref.is_active ? '' : 'bg-white'"
                      />
                      <div
                        class="flex h-5 w-5 items-center justify-center rounded-[999px]"
                        :class="pref.is_active ? 'bg-white' : ''"
                      />
                    </button>

                    <!-- Per-row action menu — kebab matches folder-row trigger styling -->
                    <DropdownMenu>
                      <DropdownMenuTrigger as-child>
                        <button
                          type="button"
                          class="flex h-6 w-6 shrink-0 cursor-pointer items-center justify-center rounded text-gray-600 opacity-0
                            transition-opacity group-hover:opacity-100 data-[state=open]:opacity-100"
                          :title="t('moreOptions')"
                          :aria-label="t('moreOptions')"
                          data-cy="setting-row-options-trigger"
                          @click.stop
                        >
                          <MoreHorizontal
                            class="h-6 w-6 fill-current"
                            :stroke-width="1.5"
                          />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent
                        align="end"
                        :side-offset="4"
                        class="w-[220px] overflow-hidden rounded-xl border-[#E5E7EB] p-0 shadow-[0px_4px_8px_0px_rgba(0,0,0,0.08)]"
                        data-cy="setting-row-options-content"
                      >
                        <DropdownMenuItem
                          class="gap-2 rounded-none border-b border-[#F9FAFB] px-3 py-2.5 text-sm text-[#1F2937]"
                          data-cy="setting-row-options-edit"
                          @click="openEditSettingForm(pref)"
                        >
                          <Pencil class="h-4 w-4 shrink-0" />
                          {{ t('settingMenuEdit') }}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          class="gap-2 rounded-none px-3 py-2.5 text-sm text-red-600"
                          data-cy="setting-row-options-delete"
                          @click="handlePreferenceRowDelete(pref)"
                        >
                          <Trash2 class="h-4 w-4 shrink-0" />
                          {{ t('settingMenuDelete') }}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </template>

                <!-- Empty state -->
                <div
                  v-else-if="!preferencesLoading"
                  class="flex h-full flex-col items-center justify-center px-6 text-center"
                >
                  <div
                    class="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-gray-100"
                  >
                    <Settings class="h-6 w-6 text-gray-400" />
                  </div>
                  <p class="text-base font-medium text-gray-900 font-inter">
                    {{ t('noSettingsFound') }}
                  </p>
                  <p class="mt-1 text-sm text-gray-500 font-inter">
                    {{ settingsEmptyMessage }}
                  </p>
                </div>
              </div>
            </div>
          </template>
        </template>
      </div>
    </DialogContent>
  </Dialog>

  <ChatFolderDialog
    :open="showCreateFolderDialog"
    :folder="editingFolder"
    @close="handleFolderDialogClose"
    @saved="handleFolderSaved"
  />

  <RenameChatDialog
    v-model:open="showRowRenameDialog"
    :current-title="actionSession?.title"
    content-class="z-[70]"
    overlay-class="z-[70]"
    @confirm="onRowRenameConfirmed"
  />
  <DeleteChatDialog
    v-model:open="showRowDeleteDialog"
    @confirm="onRowDeleteConfirmed"
  />

  <AlertDialog
    :open="showDeleteFolderDialog"
    @update:open="showDeleteFolderDialog = $event"
  >
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle>{{ t('deleteFolderTitle') }}</AlertDialogTitle>
        <AlertDialogDescription>
          {{ t('deleteFolderDescription') }}
        </AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel @click="showDeleteFolderDialog = false">
          {{ t('cancel') }}
        </AlertDialogCancel>
        <AlertDialogAction
          class="bg-red-600 text-white hover:bg-red-700"
          data-cy="confirm-delete-folder"
          @click="confirmDeleteFolder"
        >
          {{ t('deleteFolder') }}
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>

  <AlertDialog
    :open="showDeletePromptDialog"
    @update:open="showDeletePromptDialog = $event"
  >
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle>{{ t('deletePromptTitle') }}</AlertDialogTitle>
        <AlertDialogDescription>
          {{ t('deletePromptDescription') }}
        </AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel @click="showDeletePromptDialog = false">
          {{ t('cancel') }}
        </AlertDialogCancel>
        <AlertDialogAction
          class="bg-red-600 text-white hover:bg-red-700"
          data-cy="confirm-delete-prompt"
          @click="confirmDeletePrompt"
        >
          {{ t('deletePromptAction') }}
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>

  <AlertDialog
    :open="showDeletePreferenceDialog"
    @update:open="showDeletePreferenceDialog = $event"
  >
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle>{{ t('deleteSettingTitle') }}</AlertDialogTitle>
        <AlertDialogDescription>
          {{ t('deleteSettingDescription') }}
        </AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel @click="showDeletePreferenceDialog = false">
          {{ t('cancel') }}
        </AlertDialogCancel>
        <AlertDialogAction
          class="bg-red-600 text-white hover:bg-red-700"
          data-cy="confirm-delete-setting"
          @click="confirmDeletePreference"
        >
          {{ t('deleteSettingAction') }}
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
</template>
